// Source of truth: Reviewed Helper Book Vetinstant PDF & 03/09/2026 Source Content
export const KNOWLEDGE_BASE_DATA = [
  {
    "id": "patient-workbook",
    "title": "Patient Workbook",
    "description": "A comprehensive record of the patient’s care, including vitals, SOAP notes, diagnostics, procedures, clinical flow, forms, and records.",
    "topics": [
      {
        "id": "vitals",
        "title": "Vitals",
        "description": "The Vitals Dashboard allows clinic staff to record, track, and manage essential health metrics for a patient during a consultation or visit.",
        "groups": [
          {
            "id": "vitals-section",
            "title": "Vitals",
            "description": "Record, track, and manage essential health metrics.",
            "items": [
              {
                "id": "vitals-overview",
                "title": "Vitals Overview & Record Table",
                "workflow": "Patient Workbook → Vitals → Action → (+Add) → Fill in Vitals Details → Save",
                "summary": "The Vitals Dashboard allows clinic staff to record, track, and manage essential health metrics for a patient during a consultation or visit.",
                "details": [
                  {
                    "type": "text",
                    "content": "The Vitals Dashboard allows clinic staff to record, track, and manage essential health metrics for a patient during a consultation or visit."
                  },
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Vitals → Action → (+Add) → Fill in Vitals Details → Save"
                  },
                  {
                    "type": "fields",
                    "title": "Vitals Record Table Fields",
                    "items": [
                      {
                        "name": "Name (Display Field)",
                        "desc": "Displays the category or title of the vitals entry. By default, the name comes as \"Core Vitals\"."
                      },
                      {
                        "name": "Recorded Date/Time (Display Field)",
                        "desc": "Shows the exact date and timestamp of when the vitals were logged into the system."
                      },
                      {
                        "name": "Delete (Icon)",
                        "desc": "Click to remove a specific vitals entry from the patient's record."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "add-vitals",
                "title": "Add Vitals",
                "workflow": "Patient Workbook → Vitals → Action → (+Add) → Fill in Vitals Details → Save",
                "summary": "Click (+Add) to open the form and create a new vitals entry for the patient.",
                "details": [
                  {
                    "type": "text",
                    "content": "Click (+Add) to open the form and create a new vitals entry for the patient. The vitals form allows you to add the following details:"
                  },
                  {
                    "type": "fields",
                    "title": "Form Fields & Details",
                    "items": [
                      {
                        "name": "Body Weight (Input Field)",
                        "desc": "Enter the patient's body weight in kg."
                      },
                      {
                        "name": "Body Temperature (Input Field)",
                        "desc": "Enter the patient's body temperature in °F."
                      },
                      {
                        "name": "Heart Rate (Input Field)",
                        "desc": "Enter the patient's heart rate in bpm."
                      },
                      {
                        "name": "Respiratory Rate (Input Field)",
                        "desc": "Enter the patient's respiratory rate in brpm."
                      },
                      {
                        "name": "Capillary Refill Time (Drop-Down)",
                        "desc": "Select the capillary refill time in seconds."
                      },
                      {
                        "name": "Mucous Membrane Color (Drop-Down)",
                        "desc": "Select the mucous membrane color."
                      },
                      {
                        "name": "Body Condition Score (Input Field)",
                        "desc": "Enter the body condition score on the provided scale."
                      },
                      {
                        "name": "Pain Score (Drop-Down)",
                        "desc": "Select the patient's pain score from the scale."
                      },
                      {
                        "name": "Mentation (Drop-Down)",
                        "desc": "Select the patient's current mentation status."
                      },
                      {
                        "name": "Hydration Status (Drop-Down)",
                        "desc": "Select the patient's hydration level."
                      },
                      {
                        "name": "Pulse Quality (Drop-Down)",
                        "desc": "Select the patient's pulse quality."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Save (Button)",
                        "desc": "Present at the bottom of the Vitals form; click to save the entered vitals data."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "soap-notes",
        "title": "SOAP Notes",
        "description": "The SOAP Notes Dashboard allows doctors and clinic staff to record detailed clinical observations, diagnoses, and structured treatment plans.",
        "groups": [
          {
            "id": "soap-notes-section",
            "title": "SOAP Notes",
            "description": "Record clinical observations, diagnoses, and treatment plans.",
            "items": [
              {
                "id": "soap-notes-overview",
                "title": "SOAP Notes Overview & Record Table",
                "workflow": "Patient Workbook → SOAP Notes → Action → (Manual SOAP Icon) → Fill in Details → Save",
                "summary": "The SOAP Notes Dashboard allows doctors and clinic staff to record detailed clinical observations, diagnoses, and structured treatment plans for a patient during a consultation.",
                "details": [
                  {
                    "type": "text",
                    "content": "The SOAP Notes Dashboard allows doctors and clinic staff to record detailed clinical observations, diagnoses, and structured treatment plans for a patient during a consultation."
                  },
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → SOAP Notes → Action → (Manual SOAP Icon) → Fill in Details → Save"
                  },
                  {
                    "type": "fields",
                    "title": "SOAP Notes Record Table Fields",
                    "items": [
                      {
                        "name": "Name (Display Field)",
                        "desc": "Displays the sequential title of the note (e.g., \"Note 2\", \"Note 1\"). The latest entries always appear at the top of the list."
                      },
                      {
                        "name": "Recorded By (Display Field)",
                        "desc": "Displays the name of the user who created the entry."
                      },
                      {
                        "name": "Recorded Date/Time (Display Field)",
                        "desc": "Shows the exact date and timestamp of when the note was logged into the system."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "add-manual-soap-note",
                "title": "Add Manual SOAP Note",
                "workflow": "Patient Workbook → SOAP Notes → Action → (Manual SOAP Icon) → Fill in Details → Save",
                "summary": "Click the (+) Manual SOAP Icon to open the form and create a new clinical note for the patient.",
                "details": [
                  {
                    "type": "text",
                    "content": "Click the (+) Manual SOAP Icon to open the form and create a new clinical note for the patient. The form allows you to add the following details:"
                  },
                  {
                    "type": "fields",
                    "title": "Form Fields & Details",
                    "items": [
                      {
                        "name": "Diagnosis (Input Field)",
                        "desc": "Enter the clinical diagnosis for the patient."
                      },
                      {
                        "name": "Subjective (Input Field)",
                        "desc": "Record subjective observations, such as history and symptoms reported by the customer."
                      },
                      {
                        "name": "Objective (Input Field)",
                        "desc": "Record objective observations, such as physical exam findings and measurable data."
                      },
                      {
                        "name": "Assessment (Input Field)",
                        "desc": "Enter the medical assessment or analysis of the subjective and objective findings."
                      },
                      {
                        "name": "Plan (Input Field)",
                        "desc": "Detail the treatment plan, medications, or clinical next steps."
                      },
                      {
                        "name": "Conclusion (Input Field)",
                        "desc": "Enter the final conclusion or summary of the consultation."
                      },
                      {
                        "name": "Abnormal Findings (Input Field)",
                        "desc": "Document any abnormal or irregular clinical findings."
                      },
                      {
                        "name": "Customer Instructions (Input Field)",
                        "desc": "Add specific care instructions to be communicated to the pet owner."
                      },
                      {
                        "name": "Reminders (Input Field)",
                        "desc": "Note any follow-ups, rechecks, or future reminders needed for the patient."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Save (Button)",
                        "desc": "Present at the bottom of the form; click to save the newly entered SOAP note."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "edit-existing-soap-note",
                "title": "Edit Existing SOAP Note",
                "summary": "To edit a previously added note, click on the Note Name directly from the record table.",
                "details": [
                  {
                    "type": "text",
                    "content": "To edit a previously added note, click on the Note Name directly from the record table, edit the necessary details, and use the action buttons at the bottom of the form:"
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons & Pop-Up Confirmations",
                    "items": [
                      {
                        "name": "Modify (Button)",
                        "desc": "Click to apply and save the updated changes to the existing note."
                      },
                      {
                        "name": "Discard (Button)",
                        "desc": "Click to cancel your edits. Note: Clicking this will trigger a confirmation pop-up asking, \"Are you sure you want to discard the note?\" with Yes and No buttons."
                      },
                      {
                        "name": "Save (Button)",
                        "desc": "Click to save the entries."
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notifications",
                    "content": "Success Confirmation: Upon successfully clicking Save or Modify, a pop-up notification will appear at the bottom right of the screen stating: \"Changes updated successfully!\"."
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "procedures",
        "title": "Procedures",
        "description": "Records all medical and non-medical procedures performed on the patient during their visit, followed by the sections: consumable, administered, prescription.",
        "groups": [
          {
            "id": "procedures-section",
            "title": "Procedures",
            "description": "Records all medical and non-medical procedures performed on the patient during their visit.",
            "items": [
              {
                "id": "procedure-workflow",
                "title": "Procedure Workflow",
                "workflow": "Patient Workbook → Procedures → (+ Add procedure) → Fill in Procedure Details → Add Procedure",
                "summary": "Records all medical and non-medical procedures performed on the patient during their visit.",
                "details": [
                  {
                    "type": "text",
                    "content": "Records all medical and non-medical procedures performed on the patient during their visit."
                  },
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → (+ Add procedure) → Fill in Procedure Details → Add Procedure"
                  }
                ]
              },
              {
                "id": "add-a-procedure",
                "title": "Add a Procedure",
                "workflow": "Patient Workbook → Procedures → (+ Add procedure) → Fill in Procedure Details → Add Procedure",
                "summary": "Click (+ Add procedure) to open the form and add a new procedure for the patient.",
                "details": [
                  {
                    "type": "text",
                    "content": "Click (+ Add procedure) to open the form and add a new procedure for the patient. The form allows you to add the following details:"
                  },
                  {
                    "type": "fields",
                    "title": "Form Fields & Details",
                    "items": [
                      {
                        "name": "Procedure / Service (Input Field / Drop-Down)",
                        "desc": "Search and select the medical or non-medical procedure/service from the list.",
                        "helper": "Selecting a procedure with a default kit makes it available under \"Add from procedure kit\" below."
                      },
                      {
                        "name": "Variant (Drop-Down)",
                        "desc": "Select a specific variant related to the chosen procedure.",
                        "helper": "Selecting a procedure with a default kit makes it available under \"Add from procedure kit\" below."
                      },
                      {
                        "name": "Billable Qty (Input Field)",
                        "desc": "Enter the exact billable quantity for the procedure."
                      },
                      {
                        "name": "DOSE IN SERIES (Drop-Down) {For Vaccines}",
                        "desc": "Select a specific dose related to the chosen procedure."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Cancel (Button)",
                        "desc": "Click to cancel your entries and exit the form without saving."
                      },
                      {
                        "name": "Add Procedure (Button)",
                        "desc": "Click to save and add the procedure to the patient's record."
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notifications",
                    "content": "Success Confirmation: Upon successfully clicking + Add Procedure, a system notification will appear stating: \"Procedure added successfully\"."
                  }
                ]
              },
              {
                "id": "manage-added-procedures",
                "title": "Manage Added Procedures",
                "summary": "View and manage procedures added to the patient's record.",
                "details": [
                  {
                    "type": "text",
                    "content": "Once a procedure is added, it displays in the Procedures list with options to view cost breakdowns, edit details, add consumables or administered items, or delete the record."
                  }
                ]
              },
              {
                "id": "procedure-summary-card",
                "title": "Procedure Summary Card",
                "summary": "Displays a summary card detailing the costs, utilized items, and available action buttons.",
                "details": [
                  {
                    "type": "text",
                    "content": "Once a procedure is successfully added to the patient's record, it displays a summary card detailing the costs, utilized items, and available action buttons."
                  },
                  {
                    "type": "fields",
                    "title": "Record Details (Display Fields)",
                    "items": [
                      {
                        "name": "Category (Display Field)",
                        "desc": "Shows the primary category of the procedure (e.g., Surgery)."
                      },
                      {
                        "name": "Procedure (Display Field)",
                        "desc": "Displays the specific name of the added procedure (e.g., Abdominal Cryptorchid Neutering - L)."
                      },
                      {
                        "name": "Billable Qty (Display Field)",
                        "desc": "Shows the quantity billed for the procedure."
                      },
                      {
                        "name": "Package Price (Display Field)",
                        "desc": "Displays the base package price."
                      },
                      {
                        "name": "Extra (Billed) (Display Field)",
                        "desc": "Shows any additional charges for items billed separately from the package."
                      },
                      {
                        "name": "Line Total (Display Field)",
                        "desc": "Displays the final total cost (Package Price + Extra Billed)."
                      }
                    ]
                  },
                  {
                    "type": "options",
                    "title": "Options & Tags",
                    "items": [
                      {
                        "name": "Price Breakdown (Option)",
                        "desc": "Click to view the expanded cost breakdown, which displays:\n• Service Charge: The fee for the medical service itself.\n• BOM Charge: The cost of materials bundled into the package.\n• Package Price: The fixed total price (does not change with actual usage)."
                      },
                      {
                        "name": "Status Tags (Display)",
                        "desc": "Shows real-time usage tags such as \"✓4 items used from package — no extra charge\" or \"+0 extra items billed separately\"."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "edit-a-procedure",
                "title": "Edit a Procedure",
                "workflow": "Patient Workbook → Procedures → Edit → Update Details → Save Changes",
                "summary": "Click Edit (Button) to modify an existing procedure.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Edit → Update Details → Save Changes"
                  },
                  {
                    "type": "text",
                    "content": "Click the Edit (Button) to modify an existing procedure. The form displays the title \"Edit procedure — [Procedure Name]\" and allows you to update the following:"
                  },
                  {
                    "type": "fields",
                    "title": "Editable & Non-Editable Fields",
                    "items": [
                      {
                        "name": "Variant (Drop-Down)",
                        "desc": "Select or change the specific variant related to the procedure.",
                        "helper": "Selecting a procedure with a default kit makes it available under \"Add from procedure kit\" below."
                      },
                      {
                        "name": "Billable Qty (Input Field)",
                        "desc": "Update the billable quantity."
                      },
                      {
                        "name": "Service Charge (Non-Editable Field)",
                        "desc": "Displays the locked base service charge."
                      },
                      {
                        "name": "Consumables Charge (Non-Editable Field)",
                        "desc": "Displays the locked base consumables charge."
                      },
                      {
                        "name": "Package Price (Non-Editable Field)",
                        "desc": "Displays the locked total price (Service + Consumables)."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Cancel (Button)",
                        "desc": "Click to cancel your entries and exit the form without saving."
                      },
                      {
                        "name": "Save Changes (Button)",
                        "desc": "Click to save the updated procedure details."
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Procedure updated successfully\" appears after clicking Save Changes."
                  }
                ]
              },
              {
                "id": "add-items-to-a-procedure",
                "title": "Add Items to a Procedure",
                "workflow": "Patient Workbook → Procedures → + Add items → Fill Details → Add To Section → Done",
                "summary": "Click + Add items (Button) to link consumables or administered medications directly to the added procedure.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → + Add items → Fill Details → Add To Section → Done"
                  },
                  {
                    "type": "text",
                    "content": "Click the + Add items (Button) to link consumables or administered medications directly to the added procedure."
                  },
                  {
                    "type": "fields",
                    "title": "Form Fields & Details",
                    "items": [
                      {
                        "name": "Add Consumables / Administered (Section Selector)",
                        "desc": "Choose whether the item is a consumable used during the procedure or a medication administered to the patient."
                      },
                      {
                        "name": "Item (Input Field / Drop-Down)",
                        "desc": "Search and select the specific item to add."
                      },
                      {
                        "name": "Batch / Lot No. (Drop-Down)",
                        "desc": "Select the batch number (auto-fills based on the selected item)."
                      },
                      {
                        "name": "Billable Qty (Input Field)",
                        "desc": "Enter the quantity used or administered."
                      },
                      {
                        "name": "UOM (Drop-Down)",
                        "desc": "Select the unit of measurement."
                      },
                      {
                        "name": "Route (Drop-Down)",
                        "desc": "Select the administration route (e.g., oral, ear drops). Note: This field only appears if the \"Administered\" section is selected."
                      },
                      {
                        "name": "Dose (Input Field)",
                        "desc": "Select the administration dose."
                      },
                      {
                        "name": "Bill separately (Checkbox)",
                        "desc": "Check this box to charge the item as an extra line item outside of the base package.",
                        "helper": "Suggested automatically — override if this case needs something different."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Add To Consumables / Administered (Button)",
                        "desc": "Click to attach the current item to the procedure. Users can repeat this process to attach multiple items."
                      },
                      {
                        "name": "Done (Button)",
                        "desc": "Click once all items have been added to save changes and close the form."
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Item Added Successfully\" appears every time this button is clicked."
                  }
                ]
              },
              {
                "id": "delete-a-procedure",
                "title": "Delete a Procedure",
                "workflow": "Patient Workbook → Procedures → Delete (Button) → Confirm",
                "summary": "Click Delete (Button) to remove the procedure from the patient's record.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Delete (Button) → Confirm"
                  },
                  {
                    "type": "text",
                    "content": "Click the Delete (Button) to remove the procedure from the patient's record."
                  },
                  {
                    "type": "systemAction",
                    "title": "System Action & Pop-Up",
                    "content": "System Action: Deleting a procedure will also permanently delete any items from the \"Consumables\" and \"Administered\" sections that were linked to this procedure.\n\nConfirmation Pop-Up: A window will appear asking, \"Are you sure you want to delete this record?\" Click Yes to confirm deletion or No to cancel."
                  }
                ]
              }
            ]
          },
          {
            "id": "consumables-section",
            "title": "Consumables",
            "description": "Manage consumables used during procedures or visits.",
            "items": [
              {
                "id": "add-a-consumable",
                "title": "Add a Consumable",
                "workflow": "Patient Workbook → Procedures → Consumables → (+ Add item) → Fill in Details → Add Consumable",
                "summary": "Click + Add items (Button) to link consumables directly to the added procedure or record them standalone.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Consumables → (+ Add item) → Fill in Details → Add Consumable"
                  },
                  {
                    "type": "text",
                    "content": "Click the + Add items (Button) to link consumables directly to the added procedure."
                  },
                  {
                    "type": "fields",
                    "title": "Form Fields",
                    "items": [
                      {
                        "name": "Item (Input Field / Drop-Down)",
                        "desc": "Search and select the specific item to add."
                      },
                      {
                        "name": "Batch / Lot No. (Drop-Down)",
                        "desc": "Select the batch number (auto-fills based on the selected item)."
                      },
                      {
                        "name": "Billable Qty (Input Field)",
                        "desc": "Enter the quantity used or administered."
                      },
                      {
                        "name": "UOM (Drop-Down)",
                        "desc": "Select the unit of measurement."
                      },
                      {
                        "name": "LINK TO PROCEDURE (Drop-Down)",
                        "desc": "Select \"Not linked to a procedure\" to keep it separate or choose anyone of the added procedures."
                      },
                      {
                        "name": "Bill separately (Checkbox)",
                        "desc": "Check this box to charge the item as an extra line item outside of the base package.",
                        "helper": "Suggested automatically — override if this case needs something different."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Add To Consumables (Button)",
                        "desc": "Click to attach the current item to the procedure. Users can repeat this process to attach multiple items."
                      },
                      {
                        "name": "Done (Button)",
                        "desc": "Click once all items have been added to save changes and close the form."
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Item Added Successfully\" appears every time this button is clicked."
                  }
                ]
              },
              {
                "id": "consumables-summary-card",
                "title": "Consumables Summary Card",
                "summary": "Displays a summary card detailing the costs, quantity, and available action buttons.",
                "details": [
                  {
                    "type": "text",
                    "content": "Once a Consumable is successfully added to the patient's record, it displays a summary card detailing the costs, quantity, and available action buttons."
                  },
                  {
                    "type": "fields",
                    "title": "Record Details (Display Fields)",
                    "items": [
                      {
                        "name": "Name (Display Field)",
                        "desc": "Shows the name of the Consumable."
                      },
                      {
                        "name": "Billable Qty (Display Field)",
                        "desc": "Shows the quantity billed for the procedure."
                      },
                      {
                        "name": "Price (Display Field)",
                        "desc": "Displays the price."
                      },
                      {
                        "name": "Status (Display Field)",
                        "desc": "'Used', 'Requested'."
                      },
                      {
                        "name": "Batch (Display Field)",
                        "desc": "Batch details."
                      },
                      {
                        "name": "Location (Display Field)",
                        "desc": "Indicates the inventory location of the added item."
                      },
                      {
                        "name": "Linked to procedure tag (Display Field)",
                        "desc": "Displays \"Not linked\" if its not linked to a procedure, Displays procedure name if its linked to a procedure."
                      },
                      {
                        "name": "Procedure (Display Field)",
                        "desc": "Displays the specific name of the added procedure (e.g., Abdominal Cryptorchid Neutering - L) if the item is linked to it."
                      }
                    ]
                  },
                  {
                    "type": "options",
                    "title": "Options & Tags",
                    "items": [
                      {
                        "name": "Status Tags (Display)",
                        "desc": "Shows real-time usage tags such as \"Requested from Pharmacy\"."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "edit-a-consumable",
                "title": "Edit a Consumable",
                "workflow": "Patient Workbook → Procedures → Consumables → Edit → Update Details → Save Changes",
                "summary": "Click Edit (Button) to modify an existing consumable.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Consumables → Edit → Update Details → Save Changes"
                  },
                  {
                    "type": "text",
                    "content": "Click the Edit (Button) to modify an existing consumable. The form displays the title \"Edit item— [Name]\" and allows you to update the following:"
                  },
                  {
                    "type": "fields",
                    "title": "Form Fields",
                    "items": [
                      {
                        "name": "Batch / Lot No. (Drop-Down)",
                        "desc": "Select the batch number (auto-fills based on the selected item)."
                      },
                      {
                        "name": "Billable Qty (Input Field)",
                        "desc": "Enter the quantity used or administered."
                      },
                      {
                        "name": "UOM (Drop-Down)",
                        "desc": "Select the unit of measurement."
                      },
                      {
                        "name": "LINK TO PROCEDURE (Drop-Down)",
                        "desc": "Select \"Not linked to a procedure\" to keep it separate or choose anyone of the added procedures."
                      },
                      {
                        "name": "Billing status (Display field)",
                        "desc": "Displays the status."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Cancel (Button)",
                        "desc": "Click to cancel and exit the form without saving."
                      },
                      {
                        "name": "Save Changes (Button)",
                        "desc": "Click to save the updated details."
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Procedure updated successfully\" appears after clicking Save Changes."
                  }
                ]
              },
              {
                "id": "delete-a-consumable",
                "title": "Delete a Consumable",
                "workflow": "Patient Workbook → Procedures → Consumables → Delete (Button) → Confirm",
                "summary": "Click Delete (Button) to remove the consumable from the patient's record.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Consumables → Delete (Button) → Confirm"
                  },
                  {
                    "type": "text",
                    "content": "Click the Delete (Button) to remove the procedure from the patient's record."
                  },
                  {
                    "type": "systemAction",
                    "title": "System Action & Pop-Up",
                    "content": "System Action: Deleting a item will also permanently delete it from the \"Consumable \" section.\n\nConfirmation Pop-Up: A window will appear asking, \"Are you sure you want to delete this record?\" Click Yes to confirm deletion or No to cancel."
                  }
                ]
              }
            ]
          },
          {
            "id": "administer-section",
            "title": "Administer",
            "description": "Record and manage administered medications, doses, and memos.",
            "items": [
              {
                "id": "add-an-administered-item",
                "title": "Add an Administered Item",
                "workflow": "Patient Workbook → Procedures → Administer → (+ Add item) → Fill in Details → Add to Administered",
                "summary": "Click + Add items (Button) to link Administered item directly to the added procedure.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Administer → (+ Add item) → Fill in Details → Add to Administered"
                  },
                  {
                    "type": "text",
                    "content": "Click the + Add items (Button) to link Administered item directly to the added procedure."
                  },
                  {
                    "type": "fields",
                    "title": "Form Fields",
                    "items": [
                      {
                        "name": "Item (Input Field / Drop-Down)",
                        "desc": "Search and select the specific item to add."
                      },
                      {
                        "name": "Batch / Lot No. (Drop-Down)",
                        "desc": "Select the batch number (auto-fills based on the selected item)."
                      },
                      {
                        "name": "Billable Qty (Input Field)",
                        "desc": "Enter the quantity used or administered."
                      },
                      {
                        "name": "UOM (Drop-Down)",
                        "desc": "Select the unit of measurement."
                      },
                      {
                        "name": "Route (Drop-Down)",
                        "desc": "Select the administration route (e.g., oral, ear drops)."
                      },
                      {
                        "name": "Dose (Input Field)",
                        "desc": "Select the administration dose."
                      },
                      {
                        "name": "LINK TO PROCEDURE (Drop-Down)",
                        "desc": "Select \"Not linked to a procedure\" to keep it separate or choose anyone of the added procedures."
                      },
                      {
                        "name": "Bill separately (Checkbox)",
                        "desc": "Check this box to charge the item as an extra line item outside of the base package.",
                        "helper": "Suggested automatically — override if this case needs something different."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Add To Administered (Button)",
                        "desc": "Click to attach the current item to the procedure. Users can repeat this process to attach multiple items."
                      },
                      {
                        "name": "Done (Button)",
                        "desc": "Click once all items have been added to save changes and close the form."
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Item Added Successfully\" appears every time this button is clicked."
                  }
                ]
              },
              {
                "id": "controlled-substance-workflow",
                "title": "Controlled Substance Workflow",
                "summary": "Controlled Substance administration workflow.",
                "details": [
                  {
                    "type": "text",
                    "content": "Controlled Substance Workflow: View details and video guide for logging controlled substance administration."
                  }
                ]
              },
              {
                "id": "administered-summary-card",
                "title": "Administered Summary Card",
                "summary": "Displays a summary card detailing the costs, quantity, dose, route, and action buttons.",
                "details": [
                  {
                    "type": "text",
                    "content": "Once a Administered is successfully added to the patient's record, it displays a summary card detailing the costs, quantity, and available action buttons."
                  },
                  {
                    "type": "fields",
                    "title": "Record Details (Display Fields)",
                    "items": [
                      {
                        "name": "Name (Display Field)",
                        "desc": "Shows the name of the Consumable."
                      },
                      {
                        "name": "Billable Qty (Display Field)",
                        "desc": "Shows the quantity billed for the procedure."
                      },
                      {
                        "name": "Price (Display Field)",
                        "desc": "Displays the price."
                      },
                      {
                        "name": "Status (Display Field)",
                        "desc": "'Used', 'Requested'."
                      },
                      {
                        "name": "Batch (Display Field)",
                        "desc": "Batch details."
                      },
                      {
                        "name": "Location (Display Field)",
                        "desc": "Indicates the inventory location of the added item."
                      },
                      {
                        "name": "Route (Display Field)",
                        "desc": "The administration route (e.g., oral, ear drops)."
                      },
                      {
                        "name": "Dose (Display Field)",
                        "desc": "Select the administration dose."
                      },
                      {
                        "name": "Linked to procedure tag (Display Field)",
                        "desc": "Displays \"Not linked\" if its not linked to a procedure, Displays procedure name if its linked to a procedure."
                      },
                      {
                        "name": "Procedure (Display Field)",
                        "desc": "Displays the specific name of the added procedure (e.g., Abdominal Cryptorchid Neutering - L) if the item is linked to it."
                      }
                    ]
                  },
                  {
                    "type": "options",
                    "title": "Options & Tags",
                    "items": [
                      {
                        "name": "Status Tags (Display)",
                        "desc": "Shows real-time usage tags such as \"Requested from Pharmacy\"."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "edit-an-administered-item",
                "title": "Edit an Administered Item",
                "workflow": "Patient Workbook → Procedures → Administered → Edit → Update Details → Save Changes",
                "summary": "Click Edit (Button) to modify an existing administered item.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Administered → Edit → Update Details → Save Changes"
                  },
                  {
                    "type": "text",
                    "content": "Click the Edit (Button) to modify an existing consumable. The form displays the title \"Edit item— [Name]\" and allows you to update the following:"
                  },
                  {
                    "type": "fields",
                    "title": "Form Fields",
                    "items": [
                      {
                        "name": "Batch / Lot No. (Drop-Down)",
                        "desc": "Select the batch number (auto-fills based on the selected item)."
                      },
                      {
                        "name": "Billable Qty (Input Field)",
                        "desc": "Enter the quantity used or administered."
                      },
                      {
                        "name": "UOM (Drop-Down)",
                        "desc": "Select the unit of measurement."
                      },
                      {
                        "name": "Route (Drop-Down)",
                        "desc": "The administration route (e.g., oral, ear drops)."
                      },
                      {
                        "name": "Dose (Input Field)",
                        "desc": "Select the administration dose."
                      },
                      {
                        "name": "LINK TO PROCEDURE (Drop-Down)",
                        "desc": "Select \"Not linked to a procedure\" to keep it separate or choose anyone of the added procedures."
                      },
                      {
                        "name": "Billing status (Display field)",
                        "desc": "Displays the status."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Cancel (Button)",
                        "desc": "Click to cancel and exit the form without saving."
                      },
                      {
                        "name": "Save Changes (Button)",
                        "desc": "Click to save the updated details."
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Procedure updated successfully\" appears after clicking Save Changes."
                  }
                ]
              },
              {
                "id": "delete-an-administered-item",
                "title": "Delete an Administered Item",
                "workflow": "Patient Workbook → Procedures → Administered → Delete (Button) → Confirm",
                "summary": "Click Delete (Button) to remove the item from the patient's record.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Administered → Delete (Button) → Confirm"
                  },
                  {
                    "type": "text",
                    "content": "Click the Delete (Button) to remove the procedure from the patient's record."
                  },
                  {
                    "type": "systemAction",
                    "title": "System Action & Pop-Up",
                    "content": "System Action: Deleting a item will also permanently delete it from the \"Administered\" section.\n\nConfirmation Pop-Up: A window will appear asking, \"Are you sure you want to delete this record?\" Click Yes to confirm deletion or No to cancel."
                  }
                ]
              },
              {
                "id": "add-memo",
                "title": "Add Memo",
                "summary": "Click to write Memo for the administered drug or for the entire treatment.",
                "details": [
                  {
                    "type": "text",
                    "content": "Click to write Memo for the administered drug or for the entire treatment."
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Save (Button)",
                        "desc": "Click to save the memo."
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            "id": "prescription-section",
            "title": "Prescription",
            "description": "Add, edit, preview, print, and delete take-home prescriptions.",
            "items": [
              {
                "id": "add-prescription-medication",
                "title": "Add Prescription / Medication",
                "workflow": "Patient Workbook → Procedures → Prescription → (+ Add medication) → Fill in Details → Add to Prescription",
                "summary": "Click + Add items (Button) to link an item directly to the added procedure / prescription.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Prescription → (+ Add medication) → Fill in Details → Add to Prescription"
                  },
                  {
                    "type": "text",
                    "content": "Click the + Add items (Button) to link an item directly to the added procedure."
                  },
                  {
                    "type": "fields",
                    "title": "Form Fields",
                    "items": [
                      {
                        "name": "DRUG / MEDICATION (Input Field / Drop-Down)",
                        "desc": "Search and select the specific item to add."
                      },
                      {
                        "name": "Strength / Dose (Input Field)",
                        "desc": "Enter the strength."
                      },
                      {
                        "name": "Frequency (Drop-Down)",
                        "desc": "When to give that item (morning, afternoon)."
                      },
                      {
                        "name": "Route (Drop-Down)",
                        "desc": "Select the administration route (e.g., oral, ear drops)."
                      },
                      {
                        "name": "Duration (Drop-Down)",
                        "desc": "Select the duration."
                      },
                      {
                        "name": "Instructions (Drop-Down)",
                        "desc": "Select the instruction (e.g., With food, empty stomach)."
                      },
                      {
                        "name": "Remark (Input Field)",
                        "desc": "To write remarks."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Add To Prescription (Button)",
                        "desc": "Click to attach the current item to the Prescription . Users can repeat this process to attach multiple items."
                      },
                      {
                        "name": "Done (Button)",
                        "desc": "Click once all items have been added to save changes and close the form."
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Item Added Successfully\" appears every time this button is clicked."
                  }
                ]
              },
              {
                "id": "prescription-summary-card",
                "title": "Prescription Summary Card",
                "summary": "Displays a summary card detailing the dose, route, and available action buttons.",
                "details": [
                  {
                    "type": "text",
                    "content": "Once a item is successfully added to the patient's record, it displays a summary card detailing the dose, route, and available action buttons."
                  },
                  {
                    "type": "fields",
                    "title": "Record Details (Display Fields)",
                    "items": [
                      {
                        "name": "Name (Display Field)",
                        "desc": "Shows the name of the Consumable."
                      },
                      {
                        "name": "Route (Display Field)",
                        "desc": "The administration route (e.g., oral, ear drops)."
                      },
                      {
                        "name": "Dose (Display Field)",
                        "desc": "Select the administration dose."
                      },
                      {
                        "name": "Strength / Dose (Display Field)",
                        "desc": "Enter the strength."
                      },
                      {
                        "name": "Frequency (Display Field)",
                        "desc": "Show the selected frequency (morning, afternoon)."
                      },
                      {
                        "name": "Duration (Display Field)",
                        "desc": "Displays the duration."
                      },
                      {
                        "name": "Instructions (Display Field)",
                        "desc": "Displays the instruction (e.g., With food, empty stomach)."
                      },
                      {
                        "name": "Remark (Display Field)",
                        "desc": "Displays remarks."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "edit-a-prescription-item",
                "title": "Edit a Prescription Item",
                "workflow": "Patient Workbook → Procedures → Prescription → Edit → Update Details → Save Changes",
                "summary": "Click Edit (Button) to modify an existing prescription item.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Prescription → Edit → Update Details → Save Changes"
                  },
                  {
                    "type": "text",
                    "content": "Click the Edit (Button) to modify an existing consumable. The form displays the title \"Edit item— [Name]\" and allows you to update the following:"
                  },
                  {
                    "type": "fields",
                    "title": "Form Fields",
                    "items": [
                      {
                        "name": "Strength / Dose (Input Field)",
                        "desc": "Enter the strength."
                      },
                      {
                        "name": "Frequency (Drop-Down)",
                        "desc": "When to give that item (morning, afternoon)."
                      },
                      {
                        "name": "Route (Drop-Down)",
                        "desc": "Select the administration route (e.g., oral, ear drops)."
                      },
                      {
                        "name": "Duration (Drop-Down)",
                        "desc": "Select the duration."
                      },
                      {
                        "name": "Instructions (Drop-Down)",
                        "desc": "Select the instruction (e.g., With food, empty stomach)."
                      },
                      {
                        "name": "Remark (Input Field)",
                        "desc": "To write remarks."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Cancel (Button)",
                        "desc": "Click to cancel and exit the form without saving."
                      },
                      {
                        "name": "Save Changes (Button)",
                        "desc": "Click to save the updated details."
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Prescription updated successfully\" appears after clicking Save Changes."
                  }
                ]
              },
              {
                "id": "delete-a-prescription-item",
                "title": "Delete a Prescription Item",
                "workflow": "Patient Workbook → Procedures → Prescription item → Delete (Button) → Confirm",
                "summary": "Click Delete (Button) to remove the procedure / item from the patient's record.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Prescription item → Delete (Button) → Confirm"
                  },
                  {
                    "type": "text",
                    "content": "Click the Delete (Button) to remove the procedure from the patient's record."
                  },
                  {
                    "type": "systemAction",
                    "title": "System Action & Pop-Up",
                    "content": "System Action: Deleting a item will also permanently delete it from the \"Prescription \" section.\n\nConfirmation Pop-Up: A window will appear asking, \"Are you sure you want to delete this record?\" Click Yes to confirm deletion or No to cancel."
                  }
                ]
              },
              {
                "id": "add-notes",
                "title": "Add Notes",
                "summary": "Click to write Notes for the drug.",
                "details": [
                  {
                    "type": "text",
                    "content": "Click to write Notes for the drug."
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Save (Button)",
                        "desc": "Click to save the Notes."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "preview-prescription",
                "title": "Preview Prescription",
                "summary": "Preview the prescription.",
                "details": [
                  {
                    "type": "text",
                    "content": "Preview the prescription."
                  }
                ]
              },
              {
                "id": "print-prescription",
                "title": "Print Prescription",
                "summary": "Print the prescription.",
                "details": [
                  {
                    "type": "text",
                    "content": "To print the prescription."
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Print (Button)",
                        "desc": "To print the prescription."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    "id": "inventory",
    "title": "Inventory",
    "description": "The Inventory Dashboard functions as the clinic’s inventory management system, providing tools to upload inventory, monitor inventory levels, manage items and medications, and maintain an organized list of services.",
    "topics": [
      {
        "id": "inventory-dashboard",
        "title": "Inventory Dashboard",
        "description": "Overview of inventory levels, item management, and service lists.",
        "groups": [
          {
            "id": "inventory-overview-section",
            "title": "Inventory Dashboard",
            "description": "The Inventory Dashboard functions as the clinic’s inventory management system.",
            "items": [
              {
                "id": "inventory-dashboard-overview",
                "title": "Inventory Dashboard Overview",
                "summary": "The Inventory Dashboard functions as the clinic’s inventory management system, providing tools to upload inventory, monitor inventory levels, manage items and medications, and maintain an organized list of services.",
                "details": [
                  {
                    "type": "text",
                    "content": "The Inventory Dashboard functions as the clinic’s inventory management system, providing tools to upload inventory, monitor inventory levels, manage items and medications, and maintain an organized list of services."
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "stock",
        "title": "Stock",
        "description": "Manage stock creation, view all inventory items, low stock alerts, near expiry items, and out of stock records.",
        "groups": [
          {
            "id": "create-stock-section",
            "title": "Create Stock",
            "description": "Workflow and configuration options for creating new stock items.",
            "items": [
              {
                "id": "create-stock-workflow",
                "title": "Create Stock Workflow",
                "workflow": "Inventory → Stock → Action → Create Stock → Fill in Item Master Details → Configure Operational Settings → Configure UOM Mapping → Add Batch Details → Submit",
                "summary": "Workflow to create and upload new stock items with operational settings and batch details.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Inventory → Stock → Action → Create Stock → Fill in Item Master Details → Configure Operational Settings → Configure UOM Mapping → Add Batch Details → Submit"
                  },
                  {
                    "type": "fields",
                    "title": "Item Master Details",
                    "items": [
                      {
                        "name": "NDPS Drug (Check-Box)",
                        "desc": "Tick box if it's a NDPS Drug."
                      },
                      {
                        "name": "Fill up the details",
                        "desc": "Enter general product information and master attributes."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Operational Settings (Vetinstant)",
                    "items": [
                      {
                        "name": "Active (Check-Box)",
                        "desc": "Check this box if the item/medication is active, still being sold or prescribed by the clinic. If unchecked, the item becomes inactive. It remains in the system but will not appear as an available item during consultations."
                      },
                      {
                        "name": "Billable (Check-Box)",
                        "desc": "Check this box if the item/medication is billed by the clinic. Billable means the item should be billed or customer is charged for it every time it is used like vaccines or syringes; if it is unchecked the item is non-billable which means the item is used but not charged to the client (e.g. cotton rolls during surgery, disinfectants etc)."
                      },
                      {
                        "name": "Auto-Consumed (Check-Box)",
                        "desc": "Check this box when the item/medication is consumed during services and you want to charge for the item but you are unable to measure how much of the item is utilized.\n• When Auto-Consume is ON, any stock used as part of a service is automatically deducted based on the service itself, not on the exact quantity used. In this case, the cost is included in the service charge.\n• However, when an item that has auto-consume on but the item is sold or prescribed directly, the system deducts and charges for the exact quantity at its MRP.\n• Auto-consume comes in when an item is billable and non-billable based on the service."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "UOM Mapping (Sales UOM)",
                    "items": [
                      {
                        "name": "Conversion Factor (Input Field)",
                        "desc": "Conversion Factor from Purchase UOM to Sales UOM."
                      },
                      {
                        "name": "UOM (Drop-Down)",
                        "desc": "Selling unit. Add multiple layers as necessary."
                      },
                      {
                        "name": "Allow Sale (Check-Box)",
                        "desc": "Mark \"Yes\" if the given UOM is available in current branch. Default is set to \"Yes\"."
                      },
                      {
                        "name": "Threshold Qty (Input Field)",
                        "desc": "Alerts you when stock falls below this quantity."
                      },
                      {
                        "name": "Reorder Qty (Input Field)",
                        "desc": "Recommended quantity to reorder when stock is low."
                      },
                      {
                        "name": "Delete (Icon)",
                        "desc": "To delete a UOM mapping entry."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Batch Details",
                    "items": [
                      {
                        "name": "Batch Number (Input Field)",
                        "desc": "Unique ID or code stamped on a specific product."
                      },
                      {
                        "name": "Mfg Date (Date Field)",
                        "desc": "The day the item was made or packaged."
                      },
                      {
                        "name": "Expiry Date (Date Field)",
                        "desc": "The deadline after which the product shouldn't be sold or used."
                      },
                      {
                        "name": "Location (Drop-Down)",
                        "desc": "The specific shelf, aisle, or room where the item is kept."
                      },
                      {
                        "name": "Unit Qty (Input Field)",
                        "desc": "The exact physical count or amount of the item in stock."
                      },
                      {
                        "name": "UOM (Drop-Down)",
                        "desc": "How the stock is counted (eg. Bottle(s), Box(es), Pack(s)). Value will show based on what's added in UOM Mapping."
                      },
                      {
                        "name": "Selling Price (Input Field)",
                        "desc": "The price a customer pays for a single unit."
                      },
                      {
                        "name": "Pricing UOM (Input Field)",
                        "desc": "The unit used to set the price (eg., charging per Box vs. per Piece)."
                      },
                      {
                        "name": "Landed Cost (Buying) (Input Field)",
                        "desc": "Total cost to buy and deliver the item, including shipping and taxes. Auto-computed from purchases."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons & Submissions",
                    "items": [
                      {
                        "name": "Save (Button)",
                        "desc": "To save changes."
                      },
                      {
                        "name": "Discard (Button)",
                        "desc": "To discard changes."
                      },
                      {
                        "name": "Edit (Button)",
                        "desc": "To edit existing batch details."
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to save all the created stock details."
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            "id": "all-stock-section",
            "title": "All Stock",
            "description": "Displays total inventory stocks, search options, filters, bulk updates, and table views.",
            "items": [
              {
                "id": "all-stock-dashboard",
                "title": "All Stock Dashboard",
                "workflow": "Inventory → Stock → All Stock",
                "summary": "The All Stock Dashboard displays the total number of inventory stocks, providing comprehensive search fields, advanced filters, and action buttons to efficiently manage and update the inventory.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Inventory → Stock → All Stock"
                  },
                  {
                    "type": "fields",
                    "title": "Search & Sort Options",
                    "items": [
                      {
                        "name": "Generic/Trade Name (Input Field)",
                        "desc": "Enter the name to search for a specific item."
                      },
                      {
                        "name": "Barcode (Input Field)",
                        "desc": "Enter or scan the barcode to search for an item."
                      },
                      {
                        "name": "Category (Drop-Down with Checkbox)",
                        "desc": "Select one or multiple categories to filter the stock list."
                      },
                      {
                        "name": "Location (Drop-Down with Checkbox)",
                        "desc": "Select one or multiple clinic locations to filter the stock list."
                      },
                      {
                        "name": "Sorting (Toggle)",
                        "desc": "Enables A-Z or Z-A alphabetical sorting of the stock table. Note: By default, items starting with a numeric value will always appear first in both A-Z and Z-A sorting results."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Advanced Filter Options",
                    "items": [
                      {
                        "name": "Filter Checkboxes",
                        "desc": "Select checkboxes to narrow down stock list by: Status, Billable, Auto-consumed, Subcategory, HSN Code, Brand, Nature, Batch Number, Mfg Date, Expiry Date, Base UOM, Unit Quantity, Unit Selling Price, Taxes, Landed Cost, Drug Schedule, Reorder Level."
                      },
                      {
                        "name": "Save (Button)",
                        "desc": "After selecting the desired filter options, click save to apply the filter to the table."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons & Bulk Updates",
                    "items": [
                      {
                        "name": "Reset (Button)",
                        "desc": "Click to clear and reset all selected search constraints and filters."
                      },
                      {
                        "name": "Create Stock (Option)",
                        "desc": "Select from Action drop-down menu to create a new stock item."
                      },
                      {
                        "name": "Bulk Price Update (Option)",
                        "desc": "Select to update pricing for multiple items at once. Opens a window with:\n• Type (Drop-Down): Select \"Increase\" or \"Decrease\".\n• Value Type (Drop-Down): Select \"Percentage\" or \"Price\".\n• Value (Input Field): Enter numerical adjustment value.\n• Update (Button): Click to apply bulk price updates."
                      },
                      {
                        "name": "Download (Button)",
                        "desc": "Click to download the current stock table data as an Excel file."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Table Content & Display Fields",
                    "items": [
                      {
                        "name": "+ (Icon)",
                        "desc": "Click to expand the row and view specific batch details (e.g., Batch No, Mfg Date, Expiry Date, Unit Qty, UOM, Unit Selling Price, Location)."
                      },
                      {
                        "name": "Trade Name (Display Field)",
                        "desc": "Shows the commercial or brand name of the product."
                      },
                      {
                        "name": "Generic Name (Display Field)",
                        "desc": "Shows the scientific or generic name of the item."
                      },
                      {
                        "name": "Category (Display Field)",
                        "desc": "Displays the assigned category of the item."
                      },
                      {
                        "name": "Unit Qty (Display Field)",
                        "desc": "Shows the current unit quantity in stock."
                      },
                      {
                        "name": "ⓘ (Info Icon Tooltip)",
                        "desc": "Hovering over this icon reveals a tooltip displaying the \"Total Stock Value\" (e.g., Rs. 39665.00) and the exact \"Available Qty\" broken down by measurement (e.g., 52 pcs | 523 L | 5230 mcg)."
                      },
                      {
                        "name": "Status (Display Field)",
                        "desc": "Displays current operational status of item (\"Active\" or \"Inactive\")."
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            "id": "low-stock-section",
            "title": "Low Stock",
            "description": "Monitor items where current unit quantity is at or below threshold reorder levels.",
            "items": [
              {
                "id": "low-stock-dashboard",
                "title": "Low Stock Dashboard",
                "workflow": "Inventory → Stock → Low Stock",
                "summary": "The Low Stock Dashboard displays the total number of inventory low stocks, providing comprehensive search fields, advanced filters, and action buttons to efficiently manage and update the inventory.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Inventory → Stock → Low Stock"
                  },
                  {
                    "type": "fields",
                    "title": "Search, Sort & Filters",
                    "items": [
                      {
                        "name": "Search Fields",
                        "desc": "Generic/Trade Name input, Barcode input/scan, Category drop-down with checkboxes, Location drop-down with checkboxes, Sorting toggle (A-Z / Z-A)."
                      },
                      {
                        "name": "Advanced Filters",
                        "desc": "Filter by Status, Billable, Auto-consumed, Subcategory, HSN Code, Brand, Nature, Batch Number, Mfg Date, Expiry Date, Base UOM, Unit Qty, Unit Selling Price, Taxes, Landed Cost, Drug Schedule, Reorder Level."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Reset (Button)",
                        "desc": "Clear and reset all selected search constraints and filters."
                      },
                      {
                        "name": "Download (Button)",
                        "desc": "Download current low stock table data as an Excel file."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Table Display Details",
                    "items": [
                      {
                        "name": "Table Fields",
                        "desc": "+ (Icon for batch details), Trade Name, Generic Name, Category, Unit Qty, ⓘ (Info Icon for Total Stock Value & Available Qty breakdown), Status (\"Active\"/\"Inactive\")."
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            "id": "near-expiry-section",
            "title": "Near Expiry",
            "description": "Monitor stock items approaching expiration dates.",
            "items": [
              {
                "id": "near-expiry-dashboard",
                "title": "Near Expiry Dashboard",
                "workflow": "Inventory → Stock → Near Expiry",
                "summary": "The Near Expiry Stock Dashboard displays the total number of inventory stocks that is near expiry, providing comprehensive search fields, advanced filters, and action buttons to efficiently manage and update the inventory.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Inventory → Stock → Near Expiry"
                  },
                  {
                    "type": "fields",
                    "title": "Search & Filter Options",
                    "items": [
                      {
                        "name": "Expiry Date (Drop-Down)",
                        "desc": "Select the number of days to filter stock by expiry date (e.g. 0-30 days, 30-60 days, 60-90 days, 90-120 days)."
                      },
                      {
                        "name": "Generic/Trade Name & Barcode",
                        "desc": "Search by item name or barcode scan."
                      },
                      {
                        "name": "Location & Sorting",
                        "desc": "Filter by clinic location(s) and toggle A-Z / Z-A sorting."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Buttons & Table Content",
                    "items": [
                      {
                        "name": "Reset & Download",
                        "desc": "Reset filters or download near expiry stock data as Excel file."
                      },
                      {
                        "name": "Table Details",
                        "desc": "+ (Icon for batch details), Trade Name, Generic Name, Category, Unit Qty, ⓘ Info Icon tooltip, Status."
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            "id": "out-of-stock-section",
            "title": "Out of Stock",
            "description": "Monitor inventory items where Unit Quantity = 0.",
            "items": [
              {
                "id": "out-of-stock-dashboard",
                "title": "Out of Stock Dashboard",
                "workflow": "Inventory → Stock → Out of Stock",
                "summary": "The Out of Stock Dashboard displays the total number of inventory items that are out of stock (Unit Qty. = 0), providing comprehensive search fields, advanced filters, and action buttons to efficiently manage and update the inventory.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Inventory → Stock → Out of Stock"
                  },
                  {
                    "type": "text",
                    "content": "Out of Stock Status: Automatically applies to items with Unit Quantity = 0."
                  },
                  {
                    "type": "fields",
                    "title": "Search, Sort & Filters",
                    "items": [
                      {
                        "name": "Filters",
                        "desc": "Generic/Trade Name search, Barcode search, Category filter, Location filter, A-Z / Z-A sorting, Advanced Filters."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Buttons & Table Content",
                    "items": [
                      {
                        "name": "Reset & Download",
                        "desc": "Reset filters or download out of stock table data as Excel file."
                      },
                      {
                        "name": "Table Details",
                        "desc": "+ (Icon for batch details), Trade Name, Generic Name, Category, Unit Qty, ⓘ Info Icon tooltip, Status."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "service",
        "title": "Service",
        "description": "Services Dashboard, Create Service workflow, Reminder Templates, Linked Subcategories, and Variant Configurations.",
        "groups": [
          {
            "id": "services-dashboard-section",
            "title": "Services Dashboard",
            "description": "Manage clinic service offerings, filtering, bulk price updates, and status toggles.",
            "items": [
              {
                "id": "services-dashboard-overview",
                "title": "Services Dashboard Overview",
                "workflow": "Inventory → Services",
                "summary": "The Services Dashboard displays the total number of services, providing comprehensive search fields, advanced filters, and action buttons to efficiently manage and update the clinic's service offerings.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Inventory → Services"
                  },
                  {
                    "type": "fields",
                    "title": "Search & Sort Options",
                    "items": [
                      {
                        "name": "Service Name (Input Field)",
                        "desc": "Enter the name to search for a specific service."
                      },
                      {
                        "name": "Category (Drop-Down)",
                        "desc": "Select a category to filter the list."
                      },
                      {
                        "name": "Sub-Category (Drop-Down)",
                        "desc": "Select a sub-category to filter the list."
                      },
                      {
                        "name": "Type (Drop-Down)",
                        "desc": "Select the type of service (\"Medical\" or \"Non-Medical\")."
                      },
                      {
                        "name": "Sorting (Toggle)",
                        "desc": "Enables A-Z or Z-A alphabetical sorting of the services table."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Advanced Filter",
                    "items": [
                      {
                        "name": "Options",
                        "desc": "Variant ID, Variant Name, SAC Code, Price Range, Tax Applicability, Taxes."
                      },
                      {
                        "name": "Add (Button)",
                        "desc": "After selecting desired filter options, click add to apply filter to table."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons & Bulk Updates",
                    "items": [
                      {
                        "name": "Reset (Button)",
                        "desc": "Click to clear and reset all selected search constraints and filters."
                      },
                      {
                        "name": "Create Service (Option)",
                        "desc": "Select from Action menu to create a new service."
                      },
                      {
                        "name": "Enable (Option)",
                        "desc": "Select to enable a previously disabled service."
                      },
                      {
                        "name": "Bulk Price Update (Option)",
                        "desc": "Select to update pricing for multiple services at once (Type: Increase/Decrease, Value Type: Percentage/Price, Value input, Update button)."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Table Content Fields",
                    "items": [
                      {
                        "name": "Checkbox (Check-Box)",
                        "desc": "Click to select specific services (used for bulk actions)."
                      },
                      {
                        "name": "Service Name (Display Field)",
                        "desc": "Shows designated name of service."
                      },
                      {
                        "name": "Category (Display Field)",
                        "desc": "Displays assigned category."
                      },
                      {
                        "name": "Status (Display Field)",
                        "desc": "Displays current operational status (\"Active\" or \"Inactive\")."
                      },
                      {
                        "name": "Sub-Category (Display Field)",
                        "desc": "Displays assigned sub-category."
                      },
                      {
                        "name": "SAC Code (Display Field)",
                        "desc": "Shows Services Accounting Code associated with service for tax purposes."
                      },
                      {
                        "name": "Selling Price (Display Field)",
                        "desc": "Displays current price a customer pays for the service."
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            "id": "create-service-section",
            "title": "Create Service",
            "description": "Complete workflow for creating services, reminder templates, linked subcategories, and variant configurations.",
            "items": [
              {
                "id": "create-service-workflow",
                "title": "Create Service Workflow",
                "workflow": "Inventory → Service → Action → Create Service → Fill in Service Details → Add Reminder Template (if applicable) → Select Linked Subcategories → Add Service Variant → Configure Variant → Save → Submit",
                "summary": "Services define clinic offerings and link consumables, administered medications, and prescriptions directly to patient workbook consultations.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Inventory → Service → Action → Create Service → Fill in Service Details → Add Reminder Template (if applicable) → Select Linked Subcategories → Add Service Variant → Configure Variant → Save → Submit"
                  },
                  {
                    "type": "fields",
                    "title": "Service Details Form Fields",
                    "items": [
                      {
                        "name": "Disabled (Check-box)",
                        "desc": "Uncheck to disable this service so it is no longer offered."
                      },
                      {
                        "name": "Medical Type",
                        "desc": "Select Medical or Non-medical."
                      },
                      {
                        "name": "Category",
                        "desc": "Consultation, Surgery, Grooming etc."
                      },
                      {
                        "name": "Sub-Category",
                        "desc": "Based on Category (e.g., General Consultation or Follow-Up)."
                      },
                      {
                        "name": "Procedure Name",
                        "desc": "Enter the designated procedure name."
                      },
                      {
                        "name": "SAC Code",
                        "desc": "Service Accounting Code used for GST purposes."
                      },
                      {
                        "name": "Tax Applicability",
                        "desc": "Yes/No."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Reminder Template Configuration",
                    "items": [
                      {
                        "name": "TEMPLATE NAME (Input Field)",
                        "desc": "Enter name of reminder template."
                      },
                      {
                        "name": "LINKED SERVICE (Drop-down)",
                        "desc": "Select and link pre-existing service(s)."
                      },
                      {
                        "name": "WHAT DO YOU CALL ONE OCCURRENCE?",
                        "desc": "Dose."
                      },
                      {
                        "name": "STEPS",
                        "desc": "Chained steps, each due date calculated once the previous step is completed."
                      },
                      {
                        "name": "Save (Button)",
                        "desc": "Click Save to store template."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Linked Subcategories",
                    "items": [
                      {
                        "name": "Linked Subcategories",
                        "desc": "Stock subcategories whose items become searchable for every variant of this service."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Service Variants Configuration",
                    "items": [
                      {
                        "name": "Variant Id (Input Field)",
                        "desc": "Unique identifier for this variant."
                      },
                      {
                        "name": "Variant Name (Input Field)",
                        "desc": "Name that distinguishes this service variant."
                      },
                      {
                        "name": "BOM Charge",
                        "desc": "Auto-calculated cost from consumables and administered items linked to the variant."
                      },
                      {
                        "name": "Estimated Duration (in Minutes) (Input Field)",
                        "desc": "Estimated time to complete the service, in minutes."
                      },
                      {
                        "name": "Service Charge (Input Field)",
                        "desc": "Professional or service fee charged for this variant."
                      },
                      {
                        "name": "Bundled Selling Price (Input Field)",
                        "desc": "Total selling price for the bundled service (including included items)."
                      }
                    ]
                  },
                  {
                    "type": "text",
                    "content": "Variant Configuration Pop-up:\nClick \"View\" next to the Variant ID → Open configuration pop-up → Fill Consumables, Administered & Prescriptions from stock inventory drop-down → Click Save → Return to initial pop-up with Service Details → Review details and click Submit."
                  },
                  {
                    "type": "fields",
                    "title": "Stock Inventory Selection & Multiple Variants",
                    "items": [
                      {
                        "name": "Stock Inventory Drop-Down",
                        "desc": "There will be a drop-down of all the medications & items picked up from the stock inventory."
                      },
                      {
                        "name": "Multiple Service Variants & Auto Cost Updates",
                        "desc": "You can add any number of variants for each service/procedure and you can add all the consumables and medications, the system will automatically update the cost."
                      }
                    ]
                  },
                  {
                    "type": "systemAction",
                    "title": "Prerequisite Workflow Rule",
                    "content": "Note: For the best workflow, complete the stock upload before you begin setting up services."
                  },
                  {
                    "type": "notification",
                    "title": "Consultation & Real-Time Inventory Updates",
                    "content": "Automated Workflow Integration:\nOnce a service is configured, simply typing the procedure name during a consultation will automatically display the associated consumables, administered medications, and prescribed medications in the Patient Workbook.\n\nReal-Time Stock Deduction:\nEvery time a service is used, the inventory of the associated items is automatically updated in real time, ensuring stock levels remain accurate."
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "adjustments-transfer",
        "title": "Adjustments & Transfer",
        "description": "The Adjustments & Transfer Dashboard manages and tracks inventory movements. It encompasses the Transfers module, Adjustment History module, and Pending Review module.",
        "groups": [
          {
            "id": "transfers-group",
            "title": "Transfers",
            "description": "Allows clinic staff to create, manage, track, and process inventory movement requests.",
            "items": [
              {
                "id": "create-request",
                "title": "Create Request",
                "workflow": "Inventory → Adjustments & Transfer → Transfers",
                "summary": "Click Create Request (Button) to open the form and initiate a new inventory transfer.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Inventory → Adjustments & Transfer → Transfers"
                  },
                  {
                    "type": "fields",
                    "title": "Form Details",
                    "items": [
                      {
                        "name": "Request Type * (Drop-Down)",
                        "desc": "Select the nature of the request (\"Consumption\", \"Internal Transfer\", or \"External Transfer\")."
                      },
                      {
                        "name": "Requested By * (Drop-Down)",
                        "desc": "Select the name of the clinic staff member or vet making the request."
                      },
                      {
                        "name": "To Location * (Drop-Down)",
                        "desc": "Select the destination location within the clinic for the transferred items."
                      },
                      {
                        "name": "Request Date * (Calendar Input)",
                        "desc": "Select the relevant date for the transfer request."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Request Items Table",
                    "items": [
                      {
                        "name": "Product Name * (Input Field / Drop-Down)",
                        "desc": "Search and select the item to be requested."
                      },
                      {
                        "name": "Batch No. * (Input Field / Drop-Down)",
                        "desc": "Select from the available batch details (displays the batch number, location, and available quantity for the issuer to review)."
                      },
                      {
                        "name": "Available Qty @ Location (Display Field)",
                        "desc": "Displays the total available quantity based on the selected product and batch."
                      },
                      {
                        "name": "From Location (Display Field)",
                        "desc": "Displays the origin location based on the selected product and batch."
                      },
                      {
                        "name": "Issuing Qty * (Input Field)",
                        "desc": "Enter the specific quantity requested for transfer."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Delete (Button)",
                        "desc": "Click to remove a specific item row from the request table."
                      },
                      {
                        "name": "+ Add (Button)",
                        "desc": "Click to add additional items to the transfer request."
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to finalize, create, and send the request."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "transfers-record-table",
                "title": "Transfers Record Table",
                "workflow": "Inventory → Adjustments & Transfer → Transfers",
                "summary": "The main Transfers module displays a table tracking all requests.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Record Table Fields",
                    "items": [
                      {
                        "name": "Sr.No. (Display Field)",
                        "desc": "The serial or sequence number of the record."
                      },
                      {
                        "name": "Request Type (Display Field)",
                        "desc": "Shows the type of transfer."
                      },
                      {
                        "name": "Location (Display Field)",
                        "desc": "Shows the destination location."
                      },
                      {
                        "name": "Requested By (Display Field)",
                        "desc": "Displays the name of the staff member who initiated the request."
                      },
                      {
                        "name": "Requested Date (Display Field)",
                        "desc": "Displays the date the request was submitted."
                      },
                      {
                        "name": "Issue Date (Display Field)",
                        "desc": "Displays the date the request was processed and issued."
                      },
                      {
                        "name": "Status (Display Field)",
                        "desc": "Shows the current state of the request (Pending, Completed, or Cancelled)."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "action-buttons-status",
                "title": "Action Buttons & Processing",
                "summary": "Action buttons available based on transfer status.",
                "details": [
                  {
                    "type": "buttons",
                    "title": "For Status: Pending",
                    "items": [
                      {
                        "name": "Cancel (Button)",
                        "desc": "Click to immediately cancel the pending issuing/transfer request."
                      },
                      {
                        "name": "Issue (Button)",
                        "desc": "Click to open the \"Stock Request\" window and process the transfer. Displays read-only summary details and Processing Table (Product Name, Requested Qty, Batch No., From Location, Available Qty @ Location, Status tag, Issuing Qty, Tick Mark Icon to issue, Cross Icon to reject)."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "For Status: Completed or Cancelled",
                    "items": [
                      {
                        "name": "View (Button)",
                        "desc": "Click to open a read-only summary window of the finalized transfer displaying overarching request details alongside finalized table details."
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            "id": "adjustment-history-group",
            "title": "Adjustment History",
            "description": "Displays a comprehensive log of stock adjustments.",
            "items": [
              {
                "id": "adjustment-history-overview",
                "title": "Adjustment History Search & Filter Options",
                "workflow": "Inventory → Adjustments & Transfer → Adjustment History",
                "summary": "Search and filter past stock adjustments.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Inventory → Adjustments & Transfer → Adjustment History"
                  },
                  {
                    "type": "fields",
                    "title": "Search & Filter Options",
                    "items": [
                      {
                        "name": "Start Date (Calendar Input)",
                        "desc": "Select the beginning date for the search range."
                      },
                      {
                        "name": "End Date (Calendar Input)",
                        "desc": "Select the ending date for the search range."
                      },
                      {
                        "name": "Supplier (Input Field)",
                        "desc": "Enter the supplier name to filter the adjustments."
                      },
                      {
                        "name": "Customer (Input Field)",
                        "desc": "Enter the customer name to filter the adjustments."
                      },
                      {
                        "name": "Invoice Number (Input Field)",
                        "desc": "Enter a specific invoice number to search."
                      },
                      {
                        "name": "Type (Drop-Down)",
                        "desc": "Select adjustment type (\"Other Adjustments\", \"Return on Sales\")."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Reset (Button)",
                        "desc": "Click to clear and reset all selected search fields."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "create-stock-adjustment",
                "title": "Create Stock Adjustment",
                "summary": "Log a new inventory adjustment.",
                "details": [
                  {
                    "type": "text",
                    "content": "Click Create (Button) to open the form and log a new inventory adjustment titled \"Stock Adjustment\"."
                  },
                  {
                    "type": "fields",
                    "title": "Form Fields",
                    "items": [
                      {
                        "name": "Type * (Drop-Down)",
                        "desc": "Select the adjustment type (e.g., \"Other Adjustments\")."
                      },
                      {
                        "name": "Product * (Input Field / Drop-Down)",
                        "desc": "Search and select the specific product to be adjusted."
                      },
                      {
                        "name": "Batch Number * (Input Field / Drop-Down)",
                        "desc": "Select the specific batch to draw stock from (displays batch number, location, and quantity)."
                      },
                      {
                        "name": "Quantity * (Input Field)",
                        "desc": "Enter the numerical quantity of items to be adjusted."
                      },
                      {
                        "name": "UOM (Display Field)",
                        "desc": "Automatically fills with unit of measurement based on selected product and batch details."
                      },
                      {
                        "name": "Remarks * (Input Field)",
                        "desc": "Enter any necessary notes, reasons, or context for the adjustment."
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to finalize and create the stock adjustment."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "adjustment-history-table",
                "title": "Adjustment History Record Table",
                "summary": "Displays historical adjustment logs.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Record Table Fields",
                    "items": [
                      {
                        "name": "Number of count (Display Field)",
                        "desc": "Displays total number of adjustment records currently listed (e.g., Number of count: 25)."
                      },
                      {
                        "name": "# (Display Field)",
                        "desc": "Serial number or sequence of record in table."
                      },
                      {
                        "name": "Type (Display Field)",
                        "desc": "Shows type of adjustment made."
                      },
                      {
                        "name": "Reference / Reason (Display Field)",
                        "desc": "Displays reference number or reason provided."
                      },
                      {
                        "name": "Supplier / Customer Name (Display Field)",
                        "desc": "Shows associated supplier or customer name."
                      },
                      {
                        "name": "Date (Display Field)",
                        "desc": "Displays date adjustment was executed."
                      },
                      {
                        "name": "Details (Display Field)",
                        "desc": "Provides quick summary (Format: [qty number] items adjusted)."
                      },
                      {
                        "name": "View (Icon)",
                        "desc": "Click to open a detailed summary window of the specific adjustment."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "view-adjustment-summary",
                "title": "View Adjustment Summary",
                "summary": "Read-only summary window of selected adjustment record.",
                "details": [
                  {
                    "type": "text",
                    "content": "Clicking View (Icon) opens a window titled \"Stock Adjustment\" providing a read-only overview."
                  },
                  {
                    "type": "fields",
                    "title": "Summary Details & Items Table",
                    "items": [
                      {
                        "name": "Summary Details",
                        "desc": "Displays Type, Create Date, and Remarks."
                      },
                      {
                        "name": "Items Table",
                        "desc": "Table with sorting options detailing: Product, Adjusted Quantity, UOM, Batch Number, Location."
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            "id": "pending-reviews-group",
            "title": "Pending Reviews",
            "description": "Displays list of stock adjustments waiting to be reviewed.",
            "items": [
              {
                "id": "pending-reviews-overview",
                "title": "Pending Reviews Search & Filter Options",
                "workflow": "Inventory → Adjustments & Transfer → Pending Reviews",
                "summary": "Search and filter pending stock adjustment reviews.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Inventory → Adjustments & Transfer → Pending Reviews"
                  },
                  {
                    "type": "fields",
                    "title": "Search & Filter Options",
                    "items": [
                      {
                        "name": "Filters",
                        "desc": "Start Date, End Date, Supplier, Customer, Invoice Number, Type (\"Other Adjustments\", \"Return on Sales\")."
                      },
                      {
                        "name": "Reset (Button)",
                        "desc": "Click to clear and reset all selected search fields."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "pending-review-table",
                "title": "Pending Review Record Table",
                "summary": "Displays pending adjustment review records.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Record Table Fields",
                    "items": [
                      {
                        "name": "Number of count (Display Field)",
                        "desc": "Displays total count of pending reviews."
                      },
                      {
                        "name": "Table Columns",
                        "desc": "#, Type, Reference / Reason, Supplier / Customer Name, Date, Details, View Icon."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "master",
        "title": "Master",
        "description": "Configure foundational settings for Vet facility: Location, Supplier, and Inventory Settings.",
        "groups": [
          {
            "id": "location-group",
            "title": "Location",
            "description": "Create and manage clinic locations and categories.",
            "items": [
              {
                "id": "location-management",
                "title": "Location Management",
                "workflow": "Master → Location → Location",
                "summary": "Create, search, update, and delete clinic locations.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Master → Location → Location"
                  },
                  {
                    "type": "fields",
                    "title": "Create Location Form Fields",
                    "items": [
                      {
                        "name": "Location Name * (Input Field)",
                        "desc": "Enter name of new location."
                      },
                      {
                        "name": "Location Type * (Drop-Down)",
                        "desc": "Select classification (\"warehouse\", \"storage\", \"consumption\")."
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to finalize and create new location."
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notifications",
                    "content": "“location added successfully” appears after clicking Submit."
                  },
                  {
                    "type": "fields",
                    "title": "Location Record Table & Actions",
                    "items": [
                      {
                        "name": "Table Fields",
                        "desc": "Location Name, Location Type, Category, Edit Icon (Update button, notification “location updated successfully”), Delete Icon (Pop-up confirmation “Are you sure you want to delete the supplier?”, notification “location removed successfully”)."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "category-management",
                "title": "Category Management",
                "workflow": "Master → Location → Category",
                "summary": "Create and manage custom location categories.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Master → Location → Category"
                  },
                  {
                    "type": "fields",
                    "title": "Create Category & Record Table",
                    "items": [
                      {
                        "name": "Category Name * (Input Field)",
                        "desc": "Enter name of new category."
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to create category (Notification: “category added successfully”)."
                      },
                      {
                        "name": "Record Table Actions",
                        "desc": "Name field, Edit Icon (Update button, notification “category updated successfully”), Delete Icon (Pop-up confirmation “Are you sure you want to delete the category?”, notification “category removed successfully”)."
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            "id": "supplier-group",
            "title": "Supplier",
            "description": "Manage and maintain contact, tax, and business details of vendors and suppliers.",
            "items": [
              {
                "id": "supplier-management",
                "title": "Supplier Management",
                "workflow": "Master → Supplier",
                "summary": "Create, search, update, and delete supplier records.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Master → Supplier"
                  },
                  {
                    "type": "fields",
                    "title": "Create Supplier Form Fields",
                    "items": [
                      {
                        "name": "Supplier Name * (Input Field)",
                        "desc": "Enter registered name of supplier or vendor."
                      },
                      {
                        "name": "Contact Person (Input Field)",
                        "desc": "Enter name of primary contact person."
                      },
                      {
                        "name": "Email (Input Field)",
                        "desc": "Enter official email address."
                      },
                      {
                        "name": "Phone number * (Input Field)",
                        "desc": "Enter primary contact phone number."
                      },
                      {
                        "name": "GST number (Input Field)",
                        "desc": "Enter Goods and Services Tax (GST) identification number."
                      },
                      {
                        "name": "Address (Input Field)",
                        "desc": "Enter physical or billing address."
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click Submit to save (Notification: “Supplier added successfully”)."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Supplier Record Table & Actions",
                    "items": [
                      {
                        "name": "Table Fields & Actions",
                        "desc": "Supplier Name, Contact Person, Email ID, Phone Number, Address, Edit Icon (Update button, notification “supplier updated successfully”), Delete Icon (Pop-up confirmation “Are you sure you want to delete the supplier?”, notification “supplier removed successfully”)."
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            "id": "inventory-settings-group",
            "title": "Inventory Settings",
            "description": "Configure foundational categories for stock and services.",
            "items": [
              {
                "id": "stock-categories",
                "title": "Stock (Categories)",
                "workflow": "Master → Inventory Settings → Stock",
                "summary": "Create and manage broad stock categories.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Master → Inventory Settings → Stock"
                  },
                  {
                    "type": "fields",
                    "title": "Create & Record Table",
                    "items": [
                      {
                        "name": "Name (Input Field)",
                        "desc": "Enter new stock category name."
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Notification: “stock created”."
                      },
                      {
                        "name": "Record Table Actions",
                        "desc": "Category field, Edit Icon (Update button, notification “category updated successfully”), Delete Icon (Pop-up confirmation “Are you sure you want to delete the category?”, notification “category removed successfully”)."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "services-categories",
                "title": "Services (Categories)",
                "workflow": "Master → Inventory Settings → Services",
                "summary": "Create and manage broad service categories (Medical/Non-Medical).",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Master → Inventory Settings → Services"
                  },
                  {
                    "type": "fields",
                    "title": "Create & Record Table",
                    "items": [
                      {
                        "name": "Name (Input Field)",
                        "desc": "Enter new service category name."
                      },
                      {
                        "name": "Type (Drop-Down)",
                        "desc": "Select Medical or Non-Medical."
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Notification: “service created”."
                      },
                      {
                        "name": "Record Table Actions",
                        "desc": "Category, Type, Edit Icon (Update button, notification “category updated successfully”), Delete Icon (Pop-up confirmation “Are you sure you want to delete the category?”, notification “category removed successfully”)."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "download-reports",
        "title": "Download Reports",
        "description": "Generate, filter, and download detailed logs of all inventory movements for specific products.",
        "groups": [
          {
            "id": "stock-transaction-history-group",
            "title": "Stock Transaction History",
            "description": "Generate and view stock transaction logs over defined periods.",
            "items": [
              {
                "id": "generate-report-form",
                "title": "Generate Report Form",
                "workflow": "Inventory → Download Reports → Stock Transaction History",
                "summary": "Parameter window to generate specific stock transaction report.",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Inventory → Download Reports → Stock Transaction History"
                  },
                  {
                    "type": "fields",
                    "title": "Generate Form Parameters",
                    "items": [
                      {
                        "name": "Stock Name * (Input Field / Drop-Down)",
                        "desc": "Search and select specific stock product."
                      },
                      {
                        "name": "Start Date (Calendar Input)",
                        "desc": "Select beginning date."
                      },
                      {
                        "name": "End Date (Calendar Input)",
                        "desc": "Select ending date."
                      },
                      {
                        "name": "Type (Drop-Down)",
                        "desc": "Select transaction nature (\"All\", \"Sales\", \"Purchases\", \"Adjustments\", \"Returns\")."
                      },
                      {
                        "name": "Generate (Button)",
                        "desc": "Click to generate report and open detailed view window."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "detailed-report-filters",
                "title": "Detailed Report View & Advanced Filters",
                "summary": "Advanced filtering, data summary, and transaction logs.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Advanced Search & Filter Fields",
                    "items": [
                      {
                        "name": "Filter Fields",
                        "desc": "Start Date, End Date, Type, Supplier Name, Customer Name, Pet Name, Location."
                      },
                      {
                        "name": "Reset (Button)",
                        "desc": "Clear and reset advanced filter fields."
                      },
                      {
                        "name": "Download (Button)",
                        "desc": "Download generated transaction history as an Excel file."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Summary Display & Transaction Table",
                    "items": [
                      {
                        "name": "Summary Display Fields",
                        "desc": "Current Stock Qty, Total Purchase Qty, Total Sales Qty, Number of Records."
                      },
                      {
                        "name": "Transaction Record Table",
                        "desc": "Date, Type, Customer/Supplier Name, Invoice No, Batch Number, Qty Sold/Purchased, MRP."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    "id": "outpatient",
    "title": "Outpatient Dashboard",
    "description": "Track a patient's complete journey, from initial appointment booking through to a finalized consultation.",
    "topics": [
      {
        "id": "outpatient-dashboard",
        "title": "Outpatient Dashboard",
        "description": "Comprehensive view to track patient flow across consultation stages.",
        "groups": [
          {
            "id": "outpatient-workflow-group",
            "title": "Outpatient Workflow & Actions",
            "description": "Global actions, filters, workflow stages, and card actions for outpatient care.",
            "items": [
              {
                "id": "outpatient-global-actions",
                "title": "Global Actions & Filters",
                "workflow": "Outpatient → Book Appointment / Filter Vet",
                "summary": "Quick access to schedule appointments and filter patient cards by veterinarian.",
                "details": [
                  {
                    "type": "text",
                    "content": "The Outpatient dashboard provides a comprehensive view to track a patient's complete journey, from the initial appointment booking through to a finalized consultation."
                  },
                  {
                    "type": "fields",
                    "title": "Global Actions & Filters",
                    "items": [
                      {
                        "name": "Book appointment (Button)",
                        "desc": "Click to open the appointment booking window and schedule a new visit."
                      },
                      {
                        "name": "Vet Filter (Drop-Down)",
                        "desc": "By default, this is set to \"All\". Use this drop-down to filter the dashboard and view appointment summary cards assigned to a specific veterinarian."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "outpatient-stages",
                "title": "Dashboard Sections (The Patient Journey)",
                "summary": "Progressive sections representing the patient's current status at the clinic.",
                "details": [
                  {
                    "type": "text",
                    "content": "The dashboard is divided into progressive sections representing the patient's current status at the clinic. Every section includes its header name, a numeric count of the appointment cards currently within it, and a dedicated Search Field to locate specific appointment cards within that stage."
                  },
                  {
                    "type": "fields",
                    "title": "Workflow Stages",
                    "items": [
                      {
                        "name": "Upcoming",
                        "desc": "Displays appointments that are scheduled for the future."
                      },
                      {
                        "name": "Checked-in",
                        "desc": "Displays patients who have arrived at the clinic and are waiting."
                      },
                      {
                        "name": "Consultation",
                        "desc": "Displays patients who are currently inside the examination room with the veterinarian."
                      },
                      {
                        "name": "Billing",
                        "desc": "Displays patients whose medical consultation is finished and are pending invoice generation or payment."
                      },
                      {
                        "name": "Completed",
                        "desc": "Displays patients who have successfully checked out of the clinic."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "outpatient-card-layout",
                "title": "Appointment Card Layout & Actions",
                "summary": "Structured layout of individual appointment cards and context-sensitive 3-dot menu actions.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Card Display Fields",
                    "items": [
                      {
                        "name": "Patient Details (Display Fields)",
                        "desc": "Shows the pet's name, gender, species and breed, and current weight."
                      },
                      {
                        "name": "Owner Name (Display Field)",
                        "desc": "Shows the name of the pet parent."
                      },
                      {
                        "name": "Meeting (Display Field)",
                        "desc": "Displays the name of the attending doctor."
                      },
                      {
                        "name": "Facility (Display Field)",
                        "desc": "Displays the specific clinic location or facility name."
                      },
                      {
                        "name": "Date and Time (Display Field)",
                        "desc": "Shows the scheduled date and time of the consultation."
                      },
                      {
                        "name": "Dynamic Details (Display Field)",
                        "desc": "Additional details appear conditionally based on the section the card is in (e.g., \"Payment Status\" is displayed when the card enters the Billing section)."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Card Action Menu",
                    "items": [
                      {
                        "name": "3-Dot Menu (Action Icon)",
                        "desc": "Located on every card, clicking this reveals a context-sensitive menu of actions unique to the section the card is currently in."
                      },
                      {
                        "name": "Checked-in Section 3-Dot Actions Example",
                        "desc": "If the card is in the \"Checked-in\" section, the 3-dot menu displays the following actionable options: Reschedule/Reassign, Send to Consult, Update Weight, Cancel, Admit, No Show."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    "id": "inpatient",
    "title": "Inpatient Module",
    "description": "Used when a patient is admitted for hospitalization, extended treatment, intake/output monitoring, and discharge.",
    "topics": [
      {
        "id": "admit-patient-topic",
        "title": "Admit Patient",
        "description": "Initiate inpatient admission process for hospitalization or extended care.",
        "groups": [
          {
            "id": "admit-patient-group",
            "title": "Admission Form & Consent Workflow",
            "description": "Fields, deposit collection, consent form, and digital signature.",
            "items": [
              {
                "id": "admit-patient-form",
                "title": "Admission Details & Deposit Collection Form",
                "workflow": "Patient Workbook → Shortcuts → Admit Patient → Fill Details → Deposit → Consent",
                "summary": "Form details for kennel assignment, initial status, fees, and optional deposit collection.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Admission Details Form",
                    "items": [
                      {
                        "name": "Summary Card (Display Panel)",
                        "desc": "Shows a quick overview of the patient's details."
                      },
                      {
                        "name": "Assign Kennel (Drop-Down with Checkbox)",
                        "desc": "Select the specific ward or kennel for the pet's stay."
                      },
                      {
                        "name": "Initial Status (Drop-Down)",
                        "desc": "Select the pet's current condition (Options: stable, monitoring, critical, isolated)."
                      },
                      {
                        "name": "Attending Vet (Drop-Down)",
                        "desc": "Assign the primary veterinarian overseeing the admitted pet."
                      },
                      {
                        "name": "Reason for Admission / Provisional Diagnosis (Input Field)",
                        "desc": "Enter the medical reason for hospitalization."
                      },
                      {
                        "name": "Admission Exam / Processing Fee (₹) (Input Field)",
                        "desc": "Enter the initial base fee for the admission process."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Deposit Collection",
                    "items": [
                      {
                        "name": "Collect Deposit (Check-Box)",
                        "desc": "Check this box if an upfront payment is required."
                      },
                      {
                        "name": "Deposit Amount (Input Field)",
                        "desc": "Prompt: \"HOW MUCH DEPOSIT AMOUNT IS COLLECTED?\" Enter the numerical amount collected."
                      },
                      {
                        "name": "Payment Method (Drop-Down)",
                        "desc": "Select how the deposit is being paid (e.g., Card, Cash)."
                      },
                      {
                        "name": "Non-refundable (Check-Box)",
                        "desc": "Check this if the deposit cannot be returned."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "admit-patient-consent",
                "title": "Consent & Digital Signature Workflow",
                "summary": "Digital authorization, financial responsibility, and owner signature workflow.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Consent & Submit Fields",
                    "items": [
                      {
                        "name": "Owner Consent Checkbox*",
                        "desc": "\"Owner informed of estimated cost and has consented to hospitalization *\" — Must be checked to proceed."
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to generate the official consent form."
                      },
                      {
                        "name": "Disclaimer Text",
                        "desc": "\"Sign at ur own risk.\""
                      },
                      {
                        "name": "Authorization * (Check-Box)",
                        "desc": "\"I confirm I am the pet parent or authorized agent for [Pet Name] and give my informed consent for Vetinstant to proceed as described.\""
                      },
                      {
                        "name": "Financial Responsibility * (Check-Box)",
                        "desc": "\"I understand and accept financial responsibility for the cost of this treatment.\""
                      },
                      {
                        "name": "Signature of Pet Parent / Authorized Agent * (Drawing Area)",
                        "desc": "A digital whiteboard space for the owner to sign."
                      },
                      {
                        "name": "Clear Signature (Button)",
                        "desc": "Click to erase the signature space and sign again."
                      },
                      {
                        "name": "Confirm & Admit (Button)",
                        "desc": "Click to finalize the admission."
                      },
                      {
                        "name": "System Notifications",
                        "desc": "The system will sequentially display \"form added successfully\" followed by \"patient admitted successfully\"."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "inpatient-dashboard-topic",
        "title": "Inpatient Dashboard",
        "description": "Central hub to manage and track patient flow from admission through discharge.",
        "groups": [
          {
            "id": "inpatient-dashboard-group",
            "title": "Workflow Stages & Status Indicators",
            "description": "Columns, search bars, patient count, and status tags for inpatient care.",
            "items": [
              {
                "id": "inpatient-workflow-stages",
                "title": "Inpatient Dashboard Features & Workflow Stages",
                "summary": "Key features, status indicators, and 3 main workflow columns.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Key Features & Status Indicators",
                    "items": [
                      {
                        "name": "Book Appointment (Button)",
                        "desc": "A quick-access button that opens the standard appointment booking window directly from the dashboard."
                      },
                      {
                        "name": "Status Indicators",
                        "desc": "Helper text and color-coded tags: Inpatient (General Text), Critical, Stable, Monitoring, Isolation."
                      },
                      {
                        "name": "Navigation & Search",
                        "desc": "Every section title displays the total count of patients currently in that stage (e.g., Treatment & Recovery (65)). Each panel features a search bar to easily locate specific pets, and clicking on any patient summary card will immediately open that pet's Patient Workbook."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Inpatient Workflow Stages",
                    "items": [
                      {
                        "name": "1. Treatment & Recovery",
                        "desc": "Entry Point: Patient summary cards appear in this section immediately upon admission. Transition: Once medical care is complete and the bill is finalized, the patient card automatically moves to the next stage."
                      },
                      {
                        "name": "2. Billing & Discharge",
                        "desc": "Entry Point: Patients transition here from the Treatment & Recovery section once their bill is finalized. Transition: Once the payment is settled and the bill is officially closed, the patient card moves to the final stage."
                      },
                      {
                        "name": "3. Completed",
                        "desc": "Entry Point: Patient cards land here from the Billing & Discharge section after the billing process is completely closed, marking the end of the inpatient workflow."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "inpatient-care-topic",
        "title": "Patient Module (Inpatient View)",
        "description": "Overview, Vitals / I/O Monitoring, Treatment Plan, and Discharge Summary.",
        "groups": [
          {
            "id": "inpatient-care-group",
            "title": "Clinical Inpatient Care Modules",
            "description": "Comprehensive inpatient documentation tools.",
            "items": [
              {
                "id": "inpatient-overview-tab",
                "title": "Inpatient Overview",
                "summary": "Central hub for inpatient care, displaying critical patient information at a glance.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Key Sections",
                    "items": [
                      {
                        "name": "Red Flags",
                        "desc": "A dedicated space to add and highlight initial red flags or critical warnings for the patient."
                      },
                      {
                        "name": "Instructions",
                        "desc": "Allows you to add and review initial medical or care instructions for the veterinary staff."
                      },
                      {
                        "name": "Latest Vitals",
                        "desc": "Displays the most recent vital sign recordings, such as temperature, pulse, and weight. Quick Action: Click the \"Go to I/O Monitoring\" button to jump directly to the vitals module inside the Patient Workbook."
                      },
                      {
                        "name": "Tasks",
                        "desc": "Displays the immediate treatment schedule for the patient. Quick Action: Click the \"Full schedule\" shortcut to navigate directly to the Treatment Plan module for a comprehensive view of all scheduled tasks."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "inpatient-io-monitoring",
                "title": "Vitals & Intake, Output & Nursing Checks (I/O)",
                "workflow": "Patient Module → Go to I/O Monitoring → Vitals",
                "summary": "Central hub for monitoring health metrics and tracking continuous Intake, Output, and Nursing Checks.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Log Card Display Fields",
                    "items": [
                      {
                        "name": "Log Time (Display Field)",
                        "desc": "Shows the specific time the entry was recorded."
                      },
                      {
                        "name": "Category Tag (Display Field)",
                        "desc": "Displays the classification of the log."
                      },
                      {
                        "name": "Item Name (Display Field)",
                        "desc": "Shows the specific name of the administered item or check performed."
                      },
                      {
                        "name": "Quantity (Display Field)",
                        "desc": "Displays the recorded amount or volume."
                      },
                      {
                        "name": "Subtext (Display Field)",
                        "desc": "Shows the audit trail detailing who logged the entry and the exact date (e.g., Logged by Vetinstant on 03 Sep 2026)."
                      },
                      {
                        "name": "Card Action Buttons",
                        "desc": "Edit (Button): Click to modify existing log entry. Delete (Button): Click to permanently remove log entry."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Adding a Log (\"Add Log\" Window)",
                    "items": [
                      {
                        "name": "Type (Drop-Down)",
                        "desc": "Select overarching classification: Intake, Output, Nursing Check."
                      },
                      {
                        "name": "Category (Drop-Down)",
                        "desc": "Populates dynamically based on selected Type."
                      },
                      {
                        "name": "Dynamic Fields (Example: Type = Intake, Category = IV Fluid)",
                        "desc": "Fluid Type (Input Field): Enter specific fluid administered. Rate (ml/hr) (Input Field): Enter speed of fluid administration. Volume Infused So Far (ml) (Input Field): Enter total volume administered thus far."
                      },
                      {
                        "name": "Action Buttons",
                        "desc": "Cancel (Button): Discard information. Log Entry (Button): Save details and add new log."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "inpatient-treatment-plan",
                "title": "Treatment Plan Dashboard & Items",
                "workflow": "Workflow 1: Inpatient Overview → Full Schedule | Workflow 2: Patient Workbook → Treatment Plan",
                "summary": "Prescribe, schedule, and manage specific treatments for an admitted inpatient.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Treatment Plan Items Display Fields",
                    "items": [
                      {
                        "name": "Type Tag (Display Field)",
                        "desc": "Displays the classification of the treatment (e.g., Medication)."
                      },
                      {
                        "name": "Item Name (Display Field)",
                        "desc": "Shows the specific name of the prescribed treatment or drug."
                      },
                      {
                        "name": "Other Details (Display Field)",
                        "desc": "Shows dosage, administration route, and frequency (e.g., 1 · Ear Drops · Three times daily)."
                      },
                      {
                        "name": "Start Date, Time & Duration (Display Field)",
                        "desc": "Displays exactly when the treatment began and its intended length (e.g., Started 01 Sep 2026, 10:00 · 6 days)."
                      },
                      {
                        "name": "Notes (Display Field)",
                        "desc": "Displays any additional veterinarian notes or instructions regarding the treatment."
                      },
                      {
                        "name": "+ Add to Treatment Plan (Button)",
                        "desc": "Click to open the setup window and prescribe a new item for the patient's schedule."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "\"Add to Treatment Plan\" Setup Window",
                    "items": [
                      {
                        "name": "Type (Drop-Down)",
                        "desc": "Select classification: Medication, Fluid Therapy, Monitoring Check, Diet, Procedure."
                      },
                      {
                        "name": "Dynamic Fields (Example: Type = Medication)",
                        "desc": "Drug Name (Input Field with Drop-Down), Dose (Input Field), Route (Drop-Down: Ear drops, Eye drops, Intramuscular, etc.), Frequency (Drop-Down: Morning, Afternoon, Night, Once daily, Twice daily, etc.), Start (Calendar Field), Duration (Drop-Down: Days or Until discontinued — selecting Days reveals additional numeric input field for number of days), Notes (Input Field)."
                      },
                      {
                        "name": "Action Buttons",
                        "desc": "Cancel (Button): Discard information. Add to Plan (Button): Save details and add treatment to schedule."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "inpatient-discharge",
                "title": "Discharge Module & Summary Window",
                "workflow": "Patient Workbook → Discharge (Button)",
                "summary": "Finalize stay, document ultimate medical outcome, and provide detailed post-care instructions.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Discharge Summary Window Fields",
                    "items": [
                      {
                        "name": "Patient Outcome (Drop-Down)*",
                        "desc": "Select final status of patient: Recovered - Discharged to owner, Transferred to another facility, Deceased. (* mandatory field)."
                      },
                      {
                        "name": "Dynamic Fields (Example: Patient Outcome = \"Recovered - Discharged to owner\")",
                        "desc": "Final Diagnosis (Input Field)*: Enter ultimate medical diagnosis. Diet Instructions (Input Field): Specific dietary requirements or restrictions. Follow-Up Date (Calendar Field): Date for next required check-up. Activity Restrictions (Input Field): Limitations on physical activity (e.g., strictly cage rest, short leash walks only, no jumping). Owner Instructions / Warning Signs to Watch For (Input Field): Detailed post-care instructions and warning symptoms requiring immediate attention."
                      },
                      {
                        "name": "Save Changes (Button)",
                        "desc": "Click to save the entered discharge summary. Updates medical record and marks patient ready for official discharge."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    "id": "billing",
    "title": "Billing Module",
    "description": "Centralized financial hub providing access to dashboard, POS, payments, returns, master settings, and downloadable reports.",
    "topics": [
      {
        "id": "billing-dashboard-topic",
        "title": "Billing Dashboard",
        "description": "Central financial overview featuring summary cards, action buttons, recent transactions, and revenue graph.",
        "groups": [
          {
            "id": "billing-dashboard-group",
            "title": "Financial Summary & Bill Creation",
            "description": "Dashboard widgets, bill creation, and revenue analytics.",
            "items": [
              {
                "id": "billing-summary-cards",
                "title": "Summary Cards & Create Bill Window",
                "summary": "Today's Collection, Outstanding Balance, Draft Bills cards, and Create Bill modal.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Summary Cards",
                    "items": [
                      {
                        "name": "Today's Collection (Display & Shortcut)",
                        "desc": "Shows today's total collection amount, comparison percentage vs yesterday (e.g., -100% vs yesterday), and \"Today's Refund\". Action: Click \"View today’s transactions\" shortcut to jump directly to payments module with current day filter."
                      },
                      {
                        "name": "Outstanding Balance (Display & Shortcut)",
                        "desc": "Shows total outstanding balance and number of invoices it spans (e.g., across 296 invoices). Action: Click \"View all unpaid\" shortcut to jump to closed section of payments module with Unpaid and Partially Paid filters."
                      },
                      {
                        "name": "Draft Bills (Display Field)",
                        "desc": "Displays total count of draft/incomplete transactions, as well as count of completed transactions (e.g., Completed transactions: 612)."
                      },
                      {
                        "name": "Dashboard Buttons",
                        "desc": "Create (Button): Opens \"Create Bill\" window. Finalize Bill (Button): Navigates directly to \"Open\" section of Billing module to review pending bills."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "\"Create Bill\" Window",
                    "items": [
                      {
                        "name": "Bill as (Drop-Down)",
                        "desc": "Select department: Clinic, Pharmacy, Petshop."
                      },
                      {
                        "name": "Search Type (Drop-Down)",
                        "desc": "Select entity type: Customer, Pet."
                      },
                      {
                        "name": "Search Field (Input Field)",
                        "desc": "Search existing customer by name, email, or phone, or search pet by name."
                      },
                      {
                        "name": "Add new customer (Clickable Text)",
                        "desc": "Shortcut to open secondary sub-window: Parent Name*, Contact Number*, Email* -> Click Create (Button) to register and return."
                      },
                      {
                        "name": "Back dated (Checkbox)",
                        "desc": "Check if creating a bill for a past date. Selecting dynamically displays Date calendar field."
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to generate bill. Pop-up \"Bill created successfully!\" appears and system redirects to POS module."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "billing-collection-summary",
                "title": "Recent Transactions & Collection Summary Graph",
                "summary": "Top 5 recent transactions table and visual revenue graph with dynamic filters.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Recent Transactions Table",
                    "items": [
                      {
                        "name": "Transaction Details (Display Fields)",
                        "desc": "Displays top 5 most recent transactions: customer name, pet name, invoice number, total amount, status tag (Paid, Unpaid, Partially Paid)."
                      },
                      {
                        "name": "View All (Clickable Shortcut)",
                        "desc": "Click to navigate directly to closed section of payments module to view full history."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Collection Summary (Graph)",
                    "items": [
                      {
                        "name": "Source Filter (Drop-Down with Checkboxes)",
                        "desc": "Filter graph by revenue source: All, Clinic, Pet shop, Pharmacy."
                      },
                      {
                        "name": "Time Filter (Drop-Down)",
                        "desc": "Filter graph by timeframe: Today, This week, This month, This year."
                      },
                      {
                        "name": "Graph Display Details",
                        "desc": "Vertical Axis: Monetary amount range. Horizontal Axis: Payment methods (Cash, UPI, Card). Hover State: Tooltip window showing specific payment method and exact total amount collected."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "pos-topic",
        "title": "Point of Sale (POS)",
        "description": "Modify, process, apply discounts, finalize, and process payment for open bills.",
        "groups": [
          {
            "id": "pos-group",
            "title": "POS Checkout & Pay Window",
            "description": "Header details, items table, discounts, audit trail, and payment modal.",
            "items": [
              {
                "id": "pos-checkout-flow",
                "title": "POS Header, Items Table & Apply Discount",
                "summary": "Header information, line items table, line discounts/taxes, apply discount modal, and change log.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Header & Items Table Fields",
                    "items": [
                      {
                        "name": "Header Information",
                        "desc": "Date/Time, Type POS location display with \"Switch\" button to toggle locations, Unpaid Credits tag, Parent Name."
                      },
                      {
                        "name": "Items Table Columns",
                        "desc": "Service/Item Name, Qty (Quantity), Unit Price, Unit Discount, Tax, Item Total. Row buttons: Edit, Delete."
                      },
                      {
                        "name": "Table Action Buttons",
                        "desc": "Add (Button): Opens window to add new items/services. Apply Discount (Button): Opens \"Apply Discount\" window with Discount Type radio buttons (Code, Percentage, Price) -> click Add to apply."
                      },
                      {
                        "name": "Invoice Details & Summary",
                        "desc": "Vet Name drop-down, Billed By drop-down. Summary fields: Discount, Subtotal, CGST, SGST, Round Off, Total."
                      },
                      {
                        "name": "Change Log (Display Table)",
                        "desc": "Audit trail at bottom showing Date and Time, Action, Changes, and Performed by."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "pos-pay-actions",
                "title": "Primary POS Action Buttons & Pay Window",
                "summary": "Discard, Save draft, Finalize bill, Print, and Pay processing window.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Primary Action Buttons",
                    "items": [
                      {
                        "name": "Discard (Button)",
                        "desc": "Click to delete draft bill. Confirmation prompt (\"Are you sure you want to discard this record?\") appears with Confirm/Cancel options."
                      },
                      {
                        "name": "Save as draft (Button)",
                        "desc": "Click to save current progress without finalizing."
                      },
                      {
                        "name": "Finalize bill (Button)",
                        "desc": "Click to lock the invoice for payment."
                      },
                      {
                        "name": "Print (Button)",
                        "desc": "Opens preview window with checkbox option to print as \"dot matrix monospace,\" followed by final Print button."
                      },
                      {
                        "name": "Pay (Button)",
                        "desc": "Opens payment processing window."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "\"Pay Window\" Details",
                    "items": [
                      {
                        "name": "Summary Card",
                        "desc": "Displays Pet Name, Parent Name, today's date."
                      },
                      {
                        "name": "Invoice Number (Drop-Down with Checkbox)",
                        "desc": "Select specific invoice to pay."
                      },
                      {
                        "name": "Location & Amount (Display/Input Fields)",
                        "desc": "Shows invoice location, ID, total amount. Amount field defaults to total but is editable for partial payments."
                      },
                      {
                        "name": "Payment Mode (Drop-Down)",
                        "desc": "Select payment method (e.g., Cash, Card, UPI)."
                      },
                      {
                        "name": "Collection Date (Calendar Field)",
                        "desc": "Defaults to today's date."
                      },
                      {
                        "name": "Available Credit (Display Field)",
                        "desc": "Shows existing credits customer can apply."
                      },
                      {
                        "name": "Pay (Button)",
                        "desc": "Click to process transaction."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "payments-topic",
        "title": "Payments Module",
        "description": "Manage Open Bills, Closed Bills, and Collections ledgers.",
        "groups": [
          {
            "id": "payments-group",
            "title": "Payment Tracking Sub-Modules",
            "description": "Open, Closed, and Collections tracking.",
            "items": [
              {
                "id": "open-closed-bills",
                "title": "Open Bills & Closed Bills Sub-Modules",
                "summary": "Filter, search, view, process payment, cancel, return, or reopen invoices.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Open Bills Sub-Module",
                    "items": [
                      {
                        "name": "Search & Filter Options",
                        "desc": "Search fields: Pet Name, Customer Name, Vet Name, Source (All, Clinic, Petshop, Pharmacy). Filter button opens window with checkboxes: Transaction Date, Invoice Number, Invoice Type, Payment Mode, Total Due, Balance Due -> click Add. Reset (Button) clears filters."
                      },
                      {
                        "name": "Table Layout",
                        "desc": "Preceded by total count (e.g., Number of Records: 422). Table fields: Pet Name, Vet Name, Total Due, Balance Due, Source, Transaction Date, View (Button) [opens POS module]."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Closed Bills Sub-Module",
                    "items": [
                      {
                        "name": "Search & Filter Options",
                        "desc": "Search fields: Pet Name, Customer Name, Source, Status (All, Clinic, Petshop, Pharmacy). Filter checkboxes: Transaction Date, Invoice Number, Invoice Type, Payment Mode, Total Due, Balance Due, Person In Charge -> click Add. Reset (Button) clears constraints."
                      },
                      {
                        "name": "Table Layout & Action Buttons",
                        "desc": "Preceded by count (e.g., Number of Records: 629). Table fields: Pet Name, Vet Name, Total Due, Balance Due, Source, Status, Transaction Date. Action buttons: View (opens POS in read-only/payment state), Pay (opens standard Pay window), Cancel (opens cancellation window requiring Person In charge, Cancel Reason, Remarks), Return (opens return window with Return Type [Sales], Customer Name, Select Invoice -> Next), Reopen (confirmation window \"Are you sure you want to Reopen the bill?\" with Yes/Cancel options)."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "collections-ledger",
                "title": "Collections Ledger & Report Download",
                "summary": "Ledger of actual payments received, organized by receipts.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Collections Sub-Module Details",
                    "items": [
                      {
                        "name": "Search & Filter Options",
                        "desc": "Search fields: Customer Name, Pet Name, Source, Status, Payment Mode. Type (Drop-Down): All, Payment collection, Credit collection, Credit usage, Refund. Filter checkboxes: Invoice Number, Invoice Date, Payment Date, Amount Paid, Total Due, Balance Due, Discount, Taxes -> click Add. Reset (Button) clears filters."
                      },
                      {
                        "name": "Reports & Actions",
                        "desc": "Download (Button): Opens window to generate financial reports. Select Report Type and Date Range via radio buttons, click Download."
                      },
                      {
                        "name": "Table Layout",
                        "desc": "Preceded by count (e.g., Number of Records: 661). Table fields: Invoice No., Receipt No., Customer Name, Payment Date, Source, Amount Paid, Payment Mode. Record action buttons: Edit (modify receipt) and Print (generate physical copy)."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "returns-master-reports-topic",
        "title": "Returns, Master & Download Reports",
        "description": "Returns ledger, system billing master settings, and financial compliance exports.",
        "groups": [
          {
            "id": "returns-master-reports-group",
            "title": "Financial Configuration & Compliance",
            "description": "Returns, Payment Methods, Discounts, and Reports.",
            "items": [
              {
                "id": "returns-module",
                "title": "Returns Ledger & Create Return Workflow",
                "summary": "Ledger of all processed sales or purchase return transactions.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Returns Fields & Workflow",
                    "items": [
                      {
                        "name": "Search & Sort Options",
                        "desc": "Search Field (Input Field), Sort by Type (Drop-Down: Sales, Purchase)."
                      },
                      {
                        "name": "Create (Button)",
                        "desc": "Opens window: Type (Drop-Down e.g., Return of sales), Search Field (Customer) -> Submit (Button)."
                      },
                      {
                        "name": "Table Layout",
                        "desc": "Preceded by count (Number of Count). Table fields: Date, Invoice (Clickable field — opens associated invoice credit note), Reference No., Customer, Return Total, Status."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "billing-master-settings",
                "title": "Master: Payment Methods & Discount Configuration",
                "workflow": "Billing Module → Master (Drop-Down) → Payment Method / Discount",
                "summary": "Configure accepted payment methods and promotional discount rules.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Payment Method Sub-Module",
                    "items": [
                      {
                        "name": "Table Layout",
                        "desc": "S.No, Payment Method, Status (Active/Inactive), User Name, Created Date. Record Actions: Edit, Delete."
                      },
                      {
                        "name": "Create (Button)",
                        "desc": "Opens \"Create Payment Method\" window: Payment Method (Input Field), Type (Drop-Down with Checkboxes: Clinic, Petshop, Pharmacy), Status (Drop-Down: Active, Inactive) -> click Add."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Discount Sub-Module",
                    "items": [
                      {
                        "name": "Table Layout",
                        "desc": "S.No, Discount Type, Discount Code, Validity, Created by, Created Date, Action."
                      },
                      {
                        "name": "Add discount (Button)",
                        "desc": "Opens window categorized by headers: Discount Method, Minimum purchase requirements, Eligibility, Usage Restrictions -> click Save."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "billing-download-reports",
                "title": "Download Reports Module",
                "workflow": "Billing Module → Download Reports",
                "summary": "Generate, export, and download financial, operational, and regulatory compliance reports as formatted Excel files.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Available Report Types",
                    "items": [
                      {
                        "name": "Accounts Receivables Report (Option)",
                        "desc": "Detailed list of all outstanding payments, unpaid invoices, and total dues owed by customers."
                      },
                      {
                        "name": "Bills Report (Option)",
                        "desc": "Extracts a comprehensive log of all invoices and bills generated across the system within a specified timeframe."
                      },
                      {
                        "name": "Consolidated Financial Summary (Option)",
                        "desc": "High-level overview of total collections, revenues, discounts, and overall financial health across all clinic departments."
                      },
                      {
                        "name": "Day Book (Option)",
                        "desc": "Daily ledger tracking all day-to-day financial transactions, including receipts, payments, and refunds."
                      },
                      {
                        "name": "GST Report (Option)",
                        "desc": "Detailed summary of Goods and Services Tax (GST) collected on services and products for tax filing."
                      },
                      {
                        "name": "HSN Summary (Option)",
                        "desc": "Categorizes sales and associated taxes based on Harmonized System of Nomenclature (HSN) codes."
                      },
                      {
                        "name": "NDPS Drug Report (Option)",
                        "desc": "Mandatory regulatory tracking log detailing dispensing, usage, and stock of Narcotic Drugs and Psychotropic Substances (NDPS)."
                      },
                      {
                        "name": "Schedule H1 Report (Option)",
                        "desc": "Compliance log tracking prescription, sale, and patient details for regulated Schedule H1 prescription medications."
                      },
                      {
                        "name": "Statement of Account (SOA) (Option)",
                        "desc": "Extracts customer-specific chronological ledger showing historical transactions, payments made, and outstanding balances."
                      },
                      {
                        "name": "Download (Button)",
                        "desc": "Generates selected report type and automatically downloads it to local device as a Microsoft Excel file."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    "id": "vets",
    "title": "Vets Module",
    "description": "Manage clinic doctors and staff accounts, scheduling, availability, consulting hours, and vet profiles.",
    "topics": [
      {
        "id": "vets-directory-topic",
        "title": "Vets Directory & Content Dashboard",
        "description": "Search, filter, create, and edit veterinarian and staff user accounts.",
        "groups": [
          {
            "id": "vets-directory-group",
            "title": "Content Dashboard & Account Registration",
            "description": "Directory filters and account creation forms for Vets and Other staff.",
            "items": [
              {
                "id": "vets-search-filter",
                "title": "Search & Filter Options",
                "summary": "Locate vet or staff accounts using search inputs and multi-checkbox filters.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Search Fields & Filters",
                    "items": [
                      {
                        "name": "Vet Name (Input Field)",
                        "desc": "Enter name to search for a specific veterinarian or staff member."
                      },
                      {
                        "name": "Specialist (Drop-Down)",
                        "desc": "Select specific medical specialty. Options: Anesthesiology, behavioral medicine, cardiology, dentistry, dermatology, emergency and critical care, endocrinology and metabolism, gastroenterology, general medicine, Gynaecology, hematology/ immunology, hepatology, infectious disease, internal medicine, musculoskeletal, nephrology/ urology, neurology, nutrition, oncology, ophthalmology, orthopedic, radiology, respiratory, surgery, toxicology, zoological medicine. (Note: Only available when Role is set to \"Specialist\")."
                      },
                      {
                        "name": "Phone Number (Input Field)",
                        "desc": "Enter contact number to search for a specific user."
                      },
                      {
                        "name": "Email (Input Field)",
                        "desc": "Enter email address to search for a specific user."
                      },
                      {
                        "name": "Filter Option",
                        "desc": "Select checkboxes from filter menu: Type, Role, Department, Consultation Type. Click Add (Button) to apply. Click Reset (Button) to clear."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "create-user-account",
                "title": "Create an Account (Vet vs Other Staff)",
                "workflow": "Vets → Content → Create → Radio: Vet / Other",
                "summary": "Registration windows for creating new Vet or non-vet staff user accounts.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Create a Vet Account Fields",
                    "items": [
                      {
                        "name": "Name (Input Field)",
                        "desc": "Enter full name of the veterinarian."
                      },
                      {
                        "name": "Role (Drop-Down)",
                        "desc": "Options: Assistant vet, Associate vet, Consultant vet, Psychotherapist, Senior vet, Specialist, Veterinarian. (Selecting Specialist dynamically reveals Specialist drop-down with 26 medical options)."
                      },
                      {
                        "name": "Department & Associated Branches",
                        "desc": "Select department and associated clinic branches."
                      },
                      {
                        "name": "Schedulable (Check-Box)",
                        "desc": "Check if vet is available for booking appointments. Selecting dynamically displays Consultation Type (Drop-Down with Checkbox: Physical, Virtual, Home visit)."
                      },
                      {
                        "name": "Phone Number & Registration Number",
                        "desc": "Enter contact number and official medical registration/license number."
                      },
                      {
                        "name": "Account Credentials",
                        "desc": "Username (Input Field), Email (Input Field), Password (Input Field), Auto Generate Password (Check-Box)."
                      },
                      {
                        "name": "Register (Button)",
                        "desc": "Click to finalize. System Notification: \"User created successfully\" appears."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Create Other Staff Member Accounts Fields",
                    "items": [
                      {
                        "name": "Salutation (Drop-Down)",
                        "desc": "Select title (e.g., Mr., Ms., Mrs.)."
                      },
                      {
                        "name": "Name (Input Field)",
                        "desc": "Enter full name of the staff member."
                      },
                      {
                        "name": "Role (Drop-Down)",
                        "desc": "Options: Admin billing, Frontdesk, Groomer, Inventory manager, Lab/radiology technician, Pharmacist, Veterinary technician/nurse."
                      },
                      {
                        "name": "Department & Branches",
                        "desc": "Select department and assigned clinic branches."
                      },
                      {
                        "name": "Schedulable (Check-Box)",
                        "desc": "Check if staff member (like a groomer) can be booked for appointments."
                      },
                      {
                        "name": "Credentials & Register",
                        "desc": "Phone number, registration/employee ID number, username, email, password, auto generate password checkbox, and Register (Button) -> \"User created successfully\"."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "vets-user-record-table",
                "title": "User Record Table & Action Buttons",
                "summary": "List of all user accounts, profile editor modal, availability shortcut, and status toggle.",
                "details": [
                  {
                    "type": "fields",
                    "title": "User Record Table Fields",
                    "items": [
                      {
                        "name": "Number of Users (Display Field)",
                        "desc": "Displays total count of user accounts currently listed (e.g., Number of Users: 107)."
                      },
                      {
                        "name": "Name (Display Field / Interactive)",
                        "desc": "Shows full name. Clicking name opens \"Edit Profile\" window (update Physical, Virtual, Home Visit Consulting Hours, Personal Exceptions, Manage Unavailability) -> click Update (Button) -> System Notification: \"user updated successfully\"."
                      },
                      {
                        "name": "Specialty, Role, Consultation Type (Display Fields)",
                        "desc": "Displays assigned medical specialty, role, and accepted consultation types."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Row Action Buttons",
                    "items": [
                      {
                        "name": "Calendar (Icon)",
                        "desc": "Click to open \"Manage Availability\" section for that specific user (Physical, Virtual, Home Visit Hours, Exceptions, Unavailability)."
                      },
                      {
                        "name": "Activate/Deactivate (Toggle Icon)",
                        "desc": "Click to change operational status (active/inactive). Helper Text tooltip: \"Activate or Deactivate this record\". System Notification: \"Vet status updated\" appears immediately."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "vets-profile-topic",
        "title": "Vet Profile Dashboard",
        "description": "Comprehensive view of a veterinarian's account, calendar, availability, booking, and security.",
        "groups": [
          {
            "id": "vets-profile-group",
            "title": "Profile Workspace & Schedules",
            "description": "Profile header, navigation shortcuts, availability configuration, and appointment booking.",
            "items": [
              {
                "id": "vet-profile-workspace",
                "title": "Vet Profile Workspace & Shortcuts",
                "workflow": "User Profile → View Account",
                "summary": "Header details, shortcuts navigation menu, central calendar view, and right side appointments panel.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Profile Header & Shortcuts",
                    "items": [
                      {
                        "name": "Profile Header Details",
                        "desc": "Profile Photo, Name (e.g., Dr. Tony), Availability Status (Toggle Button), Consultation Type tags, Email, Phone Number."
                      },
                      {
                        "name": "Shortcuts (Left Side Menu)",
                        "desc": "Edit Profile (Option), My Calendar (Option — default view), Manage Availability (Option), Book an appointment (Option), Change Password (Option)."
                      },
                      {
                        "name": "Center Workspace",
                        "desc": "Calendar View (Display Panel): Central workspace displaying calendar matrix by days and weeks plotting scheduled appointments."
                      },
                      {
                        "name": "Appointments Panel (Right Side Menu)",
                        "desc": "Categorizes schedule into Upcoming Appointments and Past Appointments. Appointment Card fields: Patient Name (Gender), Service Type, Time Slot, Consultation Type tags, Status tag. Action: Clicking card opens Patient Workbook."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "vet-profile-edit-availability",
                "title": "Edit Profile & Manage Availability Workflows",
                "workflow": "Vet Profile → Shortcuts → Edit Profile / Manage Availability",
                "summary": "Update personal/professional details, digital signature, consulting hours, personal exceptions, and unavailability blocks.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Edit Profile Workflow",
                    "items": [
                      {
                        "name": "User Information Fields",
                        "desc": "Name, Role, Specialist (dynamic), Department, Associated Branches, Schedulable, Consultation Type (dynamic), Registration Number."
                      },
                      {
                        "name": "Add Signature (Button)",
                        "desc": "Click to open whiteboard window allowing you to draw and create user's digital signature."
                      },
                      {
                        "name": "Update (Button)",
                        "desc": "Click to save changes. System Notification: \"user updated successfully\" appears."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Manage Availability Workflow",
                    "items": [
                      {
                        "name": "Use Clinic Schedule (Toggle)",
                        "desc": "Available for Physical Consulting Hours only. Turn on to auto-fill with standard clinic schedule."
                      },
                      {
                        "name": "Manual Configuration Table",
                        "desc": "Day, Start Time (Clock Option), End Time (Clock Option). Edit (Button): enable editing. + (Icon): add multiple time slots per day. Save (Button) -> System Notification: \"changes updated successfully!\"."
                      },
                      {
                        "name": "Slot Configuration",
                        "desc": "Slot Duration (Input Block): Enter standard duration (in minutes) for each appointment slot."
                      },
                      {
                        "name": "Personal Exceptions",
                        "desc": "Start Date (Calendar Input), End Date (Calendar Input), Remarks (Input Field). Click Save (Button) to record or Delete (Icon) to remove."
                      },
                      {
                        "name": "Manage Unavailability",
                        "desc": "Date (Calendar Input), Start Time (Clock Option), End Time (Clock Option), Remarks (Input Field). Click Save (Button) to record or Delete (Icon) to remove."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "vet-book-appointment-change-pass",
                "title": "Book an Appointment & Change Password Workflows",
                "workflow": "Vet Profile → Shortcuts → Book an appointment / Change Password",
                "summary": "Form fields for booking consultations directly from vet profile and password update window.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Book an Appointment Workflow",
                    "items": [
                      {
                        "name": "Booking Form Fields",
                        "desc": "Pet / Customer Search, Booking Type* (Walk in, Phone), Consultation Mode* (Walk in defaults to Physical; Phone offers Physical, Virtual, Home visit), Service Type*, Visit Type* (New, Follow up), Reason (Input Field / Drop-Down), Vet Name* (auto-filled display), Assigned Users (Drop-Down with Checkboxes), Facility (Drop-Down), Appointment* (Radio Buttons: Immediate or Later -> Date calendar + available time slots), Remarks (Input Field)."
                      },
                      {
                        "name": "Submit (Button) & Review Modal",
                        "desc": "Click Submit to open \"Review\" window showing read-only Appointment Details and Service Details. Click Done (Button) to confirm. System Notification: \"Appointment created successfully!\" appears."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Change Password Workflow",
                    "items": [
                      {
                        "name": "Update Password Fields",
                        "desc": "Old Password (Input Field), New Password (Input Field), Confirm Password (Input Field) -> Click Save (Button) to apply."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    "id": "pets",
    "title": "Pets Module",
    "description": "Central registry for all patients, two-step registration, species master, and full Patient Module workspace.",
    "topics": [
      {
        "id": "pets-directory-topic",
        "title": "Pet Directory & Registration",
        "description": "Search, filter, two-step customer and pet registration, and record table.",
        "groups": [
          {
            "id": "pets-directory-group",
            "title": "Content Dashboard & Two-Step Registration",
            "description": "Search fields, filters, and two-step registration process.",
            "items": [
              {
                "id": "two-step-pet-registration",
                "title": "Search, Filters & Two-Step Registration",
                "workflow": "Pet → Content → Create → Step 1: Customer → Step 2: Pet",
                "summary": "Search patient directory, apply filter checkboxes, and execute two-step registration.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Search & Filter Options",
                    "items": [
                      {
                        "name": "Search Fields",
                        "desc": "Patient ID, Pet Name, Customer Name, Phone Number."
                      },
                      {
                        "name": "Filter Option (Checkboxes)",
                        "desc": "Filter by: Ownership Type, Email, Species, Color, Gender, Reproductive Status, Blood Group, Temperament. Add (Button) applies filters. Reset (Button) clears filters."
                      },
                      {
                        "name": "Create (Button)",
                        "desc": "Opens two-step registration window."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Step 1: Customer Registration",
                    "items": [
                      {
                        "name": "Search or Register New Customer",
                        "desc": "Search & select pre-registered parent OR fill: Parent Name*, Contact Number*, Email*, Secondary Phone Number, Address."
                      },
                      {
                        "name": "Next (Button)",
                        "desc": "Click to proceed to Step 2. System Notification: \"user added successfully!\" appears."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Step 2: Pet Registration",
                    "items": [
                      {
                        "name": "Summary Card & Profile Picture",
                        "desc": "Displays parent name, email, phone from Step 1. Input icon uploads profile picture."
                      },
                      {
                        "name": "Ownership Type (Radio Buttons)",
                        "desc": "Household Pet (manual name input) or Rescue/Shelter (auto-fills with generated ID e.g. Stray-7742)."
                      },
                      {
                        "name": "Clinical & Bio Fields",
                        "desc": "Name*, Species*, DOB* (Radio: Date [calendar picker] or Age [Age In input + Age Type drop-down for years/months]), Breed*, Color, Gender (Female, Male, Unknown), Reproductive Status (Intact, Neutered, Unknown), Weight, Microchip Number, Blood Group, Temperament."
                      },
                      {
                        "name": "Action Buttons",
                        "desc": "Register (Button): Save pet profile. Register+Book (Button): Save profile and open appointment booking window."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "pets-record-table",
                "title": "Pet Record Table & Action Buttons",
                "summary": "Table layout display fields and row action buttons.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Pet Record Table Fields",
                    "items": [
                      {
                        "name": "Number of Pets (Display Field)",
                        "desc": "Displays total count of registered pets (e.g., Number of Pets: 266)."
                      },
                      {
                        "name": "Columns",
                        "desc": "Patient ID, Pet Name, Breed, Gender, Customer Name (with subtext count e.g., \"no. of pets: 2\")."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Row Action Buttons",
                    "items": [
                      {
                        "name": "Activate / Deactivate (Toggle Button)",
                        "desc": "Click to change operational status of record."
                      },
                      {
                        "name": "Chat (Icon)",
                        "desc": "Click to start or continue direct messaging conversation with pet owner."
                      },
                      {
                        "name": "+ Add Pet (Button)",
                        "desc": "Click to add a new pet under this exact same customer. Bypasses Step 1 and opens Step 2 directly."
                      },
                      {
                        "name": "Book (Button)",
                        "desc": "Click to open appointment booking window and schedule visit for this pet."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "pets-master-topic",
        "title": "Pet Master (Species)",
        "description": "Configure foundational pet categories by adding and managing species.",
        "groups": [
          {
            "id": "pets-master-group",
            "title": "Species Configuration",
            "description": "Add, edit, and delete species.",
            "items": [
              {
                "id": "species-master-workflow",
                "title": "Species Master & Record Table",
                "workflow": "Pet → Master → Create Species",
                "summary": "Search, create, and manage species database records.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Search & Create Species",
                    "items": [
                      {
                        "name": "Search Bar & Create (Button)",
                        "desc": "Search by species name. Click Create to open window, enter Species Name (Input Field), click Add (Button)."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Species Record Table & Actions",
                    "items": [
                      {
                        "name": "Total Species (Display Field)",
                        "desc": "Displays total count of species (e.g., Total Species: 13)."
                      },
                      {
                        "name": "Columns",
                        "desc": "S. No, Species Name, No. of Breeds, Created By, Created Date."
                      },
                      {
                        "name": "Edit (Icon)",
                        "desc": "Opens window to update species name. Click Update (Button) to save."
                      },
                      {
                        "name": "Delete (Icon)",
                        "desc": "Click to remove species. Pop-up window: \"Are you sure you want to delete this record?\". Click Confirm (Button) to delete or Cancel (Button) to abort."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "patient-module-workspace-topic",
        "title": "Pet / Patient Module Workspace",
        "description": "Primary workspace accessed via Dashboard Appointment Card or Pet Record Table.",
        "groups": [
          {
            "id": "patient-module-left-sidebar",
            "title": "Left Menu (Sidebar)",
            "description": "Summary card, Reminders, and Shortcuts.",
            "items": [
              {
                "id": "patient-summary-card",
                "title": "Summary Card & Customer Details",
                "summary": "Read-only snapshot of pet identification and owner contact details.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Pet & Customer Details Fields",
                    "items": [
                      {
                        "name": "Pet Details (Display Fields)",
                        "desc": "Patient ID, Breed, Profile Picture, Patient Name, Gender, Demographics Summary (breed, age, weight), Color."
                      },
                      {
                        "name": "Customer Details",
                        "desc": "Edit (Button): opens window to update contact/personal details. Display fields: Customer Name, Customer Email, Phone Number, App Status (indicates if customer registered on mobile app)."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "patient-sidebar-reminders",
                "title": "Reminders Section & Item Actions",
                "summary": "Track upcoming and past-due procedures across All, Due, and Overdue status tabs.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Add a New Reminder",
                    "items": [
                      {
                        "name": "Add (Button)",
                        "desc": "Opens \"Add\" window: Type* (preventive, diagnostic, care), Item Name*, Variant*, Dose in Series*, Date*, Mark as Done (Check-Box — bypasses active reminders into History), Remarks. Click Submit (Button) -> System Notification: \"Procedure added successfully\"."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Reminder Item Display & 3-Dot Actions",
                    "items": [
                      {
                        "name": "Categories & Display",
                        "desc": "Categorized under Preventive, Diagnostics, Care with active count. Displays Item Name and Status Subtext (Due/Overdue date)."
                      },
                      {
                        "name": "1. Edit (3-Dot Action)",
                        "desc": "Opens update window -> click Submit -> System Notification: \"Procedure updated successfully!\"."
                      },
                      {
                        "name": "2. Add to Appointment (3-Dot Action)",
                        "desc": "Confirmation window: \"Are you sure you want to add it to the appointment?\" (Yes/No). Clicking Yes moves item from Reminders to Procedures section in Patient Workbook -> \"Procedure updated successfully!\"."
                      },
                      {
                        "name": "3. Delete (3-Dot Action)",
                        "desc": "Confirmation window: \"Are you sure you want to delete this record?\" (Yes/No)."
                      },
                      {
                        "name": "View all (Link/Option)",
                        "desc": "Opens full-screen \"Schedules/Reminders\" window to view, edit, delete, or add reminders across all categories."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "patient-sidebar-shortcuts",
                "title": "Detailed Shortcut Workflows",
                "summary": "Workflows for Edit Profile, Vital Trends, Book Appointment, Memos, SOA, Reminders, Certificates, Admit Patient, Collect/Refund.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Shortcut Workflows",
                    "items": [
                      {
                        "name": "Edit Profile (Option)",
                        "desc": "Opens \"Update\" window to modify pet clinical/ID info -> click Update (Button) -> System Notification: \"Pet updated successfully!\"."
                      },
                      {
                        "name": "Vital Trends (Option)",
                        "desc": "View Toggle (Temperature trends vs Weight trends graph). Graph plots historical data; hovering displays exact date, time, and numerical value."
                      },
                      {
                        "name": "Book an Appointment (Option)",
                        "desc": "Opens appointment booking window for this pet."
                      },
                      {
                        "name": "Memo/Records (Option)",
                        "desc": "Text Area or File Upload (drop zone) -> click Submit (Button) (auto-syncs to History with today's date)."
                      },
                      {
                        "name": "SOA (Statement of Accounts) (Option)",
                        "desc": "Step 1: Select Range (Radio: All or Custom [Start Date, End Date]) -> click Proceed (Button). Step 2: Review & Print (Record View panel, Print button)."
                      },
                      {
                        "name": "Reminders (Option)",
                        "desc": "Click to manage the pet's schedules and due dates. This shortcut functions identically to the \"View all\" button in the sidebar's Reminders section, opening the full-screen \"Schedules/Reminders\" window to view, edit, delete, or add new reminders across all categories (Preventive, Diagnostics, Care)."
                      },
                      {
                        "name": "Certificates (Option)",
                        "desc": "Type (Vaccine History, Travel certificate, Fitness certificate), Range (All or Custom [Start Date, End Date]) -> click Preview (Button) -> document view + Print (Button)."
                      },
                      {
                        "name": "Admit Patient (Option)",
                        "desc": "Admission form (Kennel, Initial Status, Attending Vet, Reason/Diagnosis, Fee), Deposit Collection (Collect Deposit checkbox, Deposit Amount, Payment Method, Non-refundable checkbox), Consent & Submit (Owner informed checkbox -> Submit -> Consent form with \"Sign at ur own risk\", Authorization checkbox, Financial Responsibility checkbox, Signature whiteboard, Clear Signature button, Confirm & Admit button) -> Notifications: \"form added successfully\" then \"patient admitted successfully\"."
                      },
                      {
                        "name": "Collect/Refund (Option)",
                        "desc": "Parent Name, Available Balance, Credit Balance radio (Collect, Refund), Amount, Payment Mode (Cash, Card, UPI), Reason -> click Pay (Button)."
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            "id": "patient-module-center-right",
            "title": "Center Workspace & Right Side Panel",
            "description": "Overview, History, Active Appointments, Internal Tags/Notes, Billing Snapshot, Other Pets.",
            "items": [
              {
                "id": "patient-center-overview-history",
                "title": "Center Workspace: Overview & History Tabs",
                "summary": "Current visit snapshot, allergies, active meds, diagnostics, and chronological medical history.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Overview Tab",
                    "items": [
                      {
                        "name": "Appointment Details",
                        "desc": "Edit (Button), Meeting doctor, Schedule, Tags, Remarks."
                      },
                      {
                        "name": "Allergies & Active Medications",
                        "desc": "Allergy List display, Tick (Icon) to manage/add allergy -> Save. Active Medications display section."
                      },
                      {
                        "name": "Diagnostics Cards & View Window",
                        "desc": "Grid of diagnostic cards (Name, Date & Time, Status). View (Button) opens Diagnostic View Window: Name, Type, Variant, Performed By, Diagnostic Report Template input, Result Status (preliminary, final), Status (send for clinician's review, pending patient review, completed) -> click Save (Button) -> System Notification: \"Changes updated successfully!\"."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "History Tab",
                    "items": [
                      {
                        "name": "Search & Filter Options",
                        "desc": "Search fields (Type checkboxes, Vet Name checkboxes, Case ID checkboxes, System checkboxes [general, ear, emergency, eye]). Filter option (Condition Status, Condition, Date -> click Add). Reset (Button) clears constraints."
                      },
                      {
                        "name": "History Record Layout",
                        "desc": "Card fields: Month & Year, Case ID, Consultation Tag, Consultation & System, Exact Date, Exact Time, Vet Name. Procedures Table: Name, Category. Print (Button) prints physical copy."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "patient-right-side-panel",
                "title": "Right Side Panel Details",
                "summary": "Active appointments, internal tags/notes, billing snapshot, and family pets.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Right Panel Features",
                    "items": [
                      {
                        "name": "Active Appointments",
                        "desc": "Active appointment count + Show All option (lists past appointments; clicking any directs user to its Patient Workbook)."
                      },
                      {
                        "name": "Internal Tags",
                        "desc": "Edit (Button) opens \"Internal tags\" window: Color icon, Search/Write Tag input, Add button, Save button -> System Notification: \"Internal Tags added successfully!\"."
                      },
                      {
                        "name": "Internal Notes",
                        "desc": "Edit (Button) opens \"Internal Notes\" window: Note History panel (writer, content, timestamp), Write Note input, Add button -> System Notification: \"Internal Note added successfully!\"."
                      },
                      {
                        "name": "Billing Snapshot & Pay Window",
                        "desc": "Outstanding balance, Credits. View (Button) opens Outstanding Due view with line View icon, Total due, and Pay (Button) -> Pay Window: Invoice Number (All or specific), Total Balance Due, Amount, Payment Mode, Collection Date -> click Pay (Button)."
                      },
                      {
                        "name": "Other Pets",
                        "desc": "Profile pictures and names of other pets under same owner. + (Icon) opens Step 2 Pet Registration directly."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    "id": "plan",
    "title": "Plan Module",
    "description": "Tools for administrators to create, configure, and manage standard operational forms and templates.",
    "topics": [
      {
        "id": "plan-forms-topic",
        "title": "Consent Forms & Protocol Forms",
        "description": "Legal consent forms and custom step-by-step treatment protocols.",
        "groups": [
          {
            "id": "plan-forms-group",
            "title": "Forms & Protocols Builder",
            "description": "Consent and Protocol forms configuration.",
            "items": [
              {
                "id": "consent-forms-builder",
                "title": "Consent Forms Builder",
                "workflow": "Plan → Consent Forms → Create",
                "summary": "Create legal consent forms with placeholder signatures and preview/print options.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Consent Form Fields & Actions",
                    "items": [
                      {
                        "name": "Search Field",
                        "desc": "Search for existing form by name."
                      },
                      {
                        "name": "Create Window Fields",
                        "desc": "Name (Input Field), Service Type (Drop-Down: Grooming, Lab tests, Surgery, Vaccination, Others), Linked Services (Drop-Down with Checkboxes), Content (Input Field), Signatures (Display Texts for pet parent and vet)."
                      },
                      {
                        "name": "Action Buttons & Table Layout",
                        "desc": "Preview (Button) (document view + Print), Save (Button). Table fields: Name, Service Type, Created By, Last Updated. Icons: Edit (Icon), Delete (Icon) -> confirmation prompt \"Are you sure you want to delete the form?\" (Yes/No)."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "protocol-forms-builder",
                "title": "Protocol Forms Two-Step Builder",
                "workflow": "Plan → Protocol Forms → Create",
                "summary": "Build custom step-by-step treatment or assessment protocols.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Step 1: Configuration & Builder",
                    "items": [
                      {
                        "name": "Protocol Header Fields",
                        "desc": "Name (Input Field)*, Species (Drop-Down with Checkboxes)*, Type (Drop-Down: Outpatient, Inpatient)*, Description (Input Field)."
                      },
                      {
                        "name": "Question Builder",
                        "desc": "Question 1 (Input Field), Answer Type (Drop-Down: Checkboxes, File upload, Multiple choice, Long text, Short text), Delete (Icon), + Add (Button) to add questions, Next (Button) to proceed to Step 2."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Step 2: Review & Submit",
                    "items": [
                      {
                        "name": "Review & Actions",
                        "desc": "Review Header summary, live question preview, Back (Button), Submit (Button) -> Pop-up confirms \"Treatment plan created successfully!\". Table fields: Name, Species, Created By, Last Updated, Edit/Delete icons."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "plan-parameters-templates-topic",
        "title": "Parameters & Templates",
        "description": "Medical parameters, diagnostic reporting templates, reminder templates, and certificates.",
        "groups": [
          {
            "id": "plan-parameters-templates-group",
            "title": "Configuration Tools",
            "description": "Parameters, diagnostic templates, reminder chains, and certificates.",
            "items": [
              {
                "id": "parameters-group-management",
                "title": "Parameters & Reference Ranges",
                "summary": "Group measurement parameters with species-specific lower and upper reference ranges.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Group & Parameter Fields",
                    "items": [
                      {
                        "name": "Group Management",
                        "desc": "Search Field, Create Group (Button) -> Group Name (Input Field) + Add. Table: S.No, Group Name (clickable), Created By, Last Updated, Edit/Delete icons."
                      },
                      {
                        "name": "Add Parameter Window",
                        "desc": "Parameter Name (Input Field), Input Type (Drop-Down: Numeric, Text, Drop-down). Dynamic Field: If Numeric, Units input appears with Add Reference button (Species drop-down, Lower Range input, Upper Range input). Group Name drop-down, Save (Button). Table: Parameter Name, Unit, Input Type, Reference Range (Species), Group, Created By, Created Date, Edit/Delete buttons."
                      }
                    ]
                  }
                ]
              },
              {
                "id": "diagnostic-reminder-templates",
                "title": "Diagnostic Templates & Automated Reminder Sequences",
                "workflow": "Plan → Diagnostic Template / Reminder Template → Create",
                "summary": "Build multi-step diagnostic report templates and chained follow-up reminder sequences.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Diagnostic Template Builder",
                    "items": [
                      {
                        "name": "Step 1 Setup",
                        "desc": "Template Name*, Type* (Parameter, Report), Linked Service (Drop-Down with Checkboxes) -> Save."
                      },
                      {
                        "name": "Step 2 & Step 3",
                        "desc": "Add Field button, Add Parameter button -> Next -> Step 3 Review & Submit (live preview, Back, Submit -> pop-up \"Template created successfully!\"). Table: Name, Created By, Linked Services, Last Update, Edit button."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Reminder Template Chained Configuration",
                    "items": [
                      {
                        "name": "Header Fields",
                        "desc": "Template Name (Input Field), Linked Service (Input Field with Drop-Down), What do you call one occurrence? (Default text: \"dose\")."
                      },
                      {
                        "name": "Chained Dose Configuration",
                        "desc": "Dose 1: \"Completed at time of service — starting point\". Add Dose (Button) reveals dynamic fields: Due (\"[ X ] days after previous step's completed date\"), Remind (\"[ X ] days before due, then every...\"), Interval (\"...[ X ] days, until completed or...\"), Cut-off (\"...[ X ] days after due — whichever comes first\"), Remove (Button), Save (Button)."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Certificates Sub-Module",
                    "items": [
                      {
                        "name": "Record Action Buttons",
                        "desc": "Preview (Button) (opens preview with Print and Download as PDF buttons), Edit (Button) (opens editable template with Save button)."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    "id": "support",
    "title": "Support Module",
    "description": "Report, track, and manage tickets regarding system-related issues or feature inquiries.",
    "topics": [
      {
        "id": "support-tickets-topic",
        "title": "Support Tickets",
        "description": "Ticket creation and Active/Closed status tracking.",
        "groups": [
          {
            "id": "support-tickets-group",
            "title": "Ticket Management",
            "description": "Issue reporting and query progress tracking.",
            "items": [
              {
                "id": "create-support-ticket",
                "title": "Creating & Tracking Support Tickets",
                "workflow": "Support → Create ticket → Fill Details → Submit",
                "summary": "Categorize system issues, attach screenshots, and track query cards across Active and Closed sections.",
                "details": [
                  {
                    "type": "fields",
                    "title": "Create Ticket Setup Window",
                    "items": [
                      {
                        "name": "Category (Drop-Down)",
                        "desc": "Options: Login & Account, Appointments & Consultation, Pet & Client Records, EMR & Medical Records, Billing & Payments, Inventory & Pharmacy, Diagnostics, Reports & Documents, etc."
                      },
                      {
                        "name": "Reason (Drop-Down)",
                        "desc": "Populates dynamically based on selected Category."
                      },
                      {
                        "name": "Problem Type (Drop-Down)",
                        "desc": "Options: I can't do something, Something is not working, Something is incorrect, Something is missing, I don't know how to do something, I need a change/enhancement."
                      },
                      {
                        "name": "Description & Attachment",
                        "desc": "Description (Input Field) detailing steps leading to error. Attachment (Select Field): upload screenshots/images."
                      },
                      {
                        "name": "Action Buttons",
                        "desc": "Cancel (Button): discard ticket. Submit (Button): officially raise ticket to support team."
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Ticket Tracking Sections & Display Cards",
                    "items": [
                      {
                        "name": "Tracking Sections",
                        "desc": "Active (Section): open queries being investigated. Closed (Section): resolved and closed queries."
                      },
                      {
                        "name": "Summary Card Display Fields",
                        "desc": "Ticket Type, Ticket ID (e.g., Ticket_20260904103925), Status (OPEN or CLOSED), Tag (\"RESPONSE RECEIVED\" tag visible on Active tickets when support replied), Description preview, Date & Time (e.g., 04 Sep 2026, 10:39 AM)."
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  }
];
